import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  doc,
  getDoc,
  updateDoc,
  addDoc,
  orderBy,
  getDocs,
  limit,
  deleteDoc,
  query,
  where,
} from "firebase/firestore";
import html2pdf from "html2pdf.js";
import autoTable from "jspdf-autotable";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../utils/firebase.js";
import jsPDF from "jspdf";
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Bar,
  BarChart,
  LabelList,
} from "recharts";
import {
  DollarSign,
  Plus,
  Save,
  Trash2,
  Calendar,
  Edit2,
  Download,
  ChevronRight,
  ChevronDown,
  FileText,
  X,
  TestTube,
  Home,
  Phone,
  ClipboardList,
  Pill,
  Heart,
  Scale,
  User,
  LogOut,
} from "lucide-react";
import { useParams } from "react-router-dom";
import Select from "react-select";
import Modal from "react-modal";
import { useAuth } from "../hooks/AuthenticationProvider";
import FormListModal from "./Modals/FormListModal";
import { findMedicationSuggestions } from "../utils/medicationList";

const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return "";
  return phoneNumber.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
};

// Helper function to format field keys
const formatFieldKey = (key) => {
  // Extract field name without the timestamp
  const parts = key.split("-");
  const fieldType = parts[0];

  // Format the field type name nicely
  return fieldType
    .replace(/([A-Z])/g, " $1") // Add space before capital letters
    .replace(/-/g, " ") // Replace hyphens with spaces
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className="custom-tooltip"
        style={{
          backgroundColor: "black",
          padding: "10px",
          borderRadius: "5px",
        }}
      >
        <p
          className="label"
          style={{ color: "white" }}
        >{`Questionnaire: ${payload[0].payload.questionnaire}`}</p>
        {payload
          .filter(
            (entry) =>
              entry.value !== null &&
              entry.value !== undefined &&
              entry.value !== ""
          ) // Filter out items without value
          .map((entry, index) => (
            <p key={`item-${index}`} style={{ color: entry.color }}>
              {`${entry.name}: ${entry.value}`}
            </p>
          ))}
      </div>
    );
  }

  return null;
};

const getStrokeColor = (index) => {
  const colors = [
    "#8884d8",
    "#82ca9d",
    "#ffc658",
    "#ff7300",
    "#413ea0",
    "#E91E63",
    "#673AB7",
    "#00BCD4",
    "#4CAF50",
    "#FFC107",
  ];
  return colors[index % colors.length];
};

const DomainScoreCard = ({
  label,
  score,
  colorClass,
  borderClass,
  textClass,
  allQuestionnaires,
}) => {
  const [prevScore, setPrevScore] = useState(null);

  useEffect(() => {
    const sortedQuestionnaires = [...allQuestionnaires].sort(
      (a, b) =>
        new Date(b.timestamp?.seconds * 1000) -
        new Date(a.timestamp?.seconds * 1000)
    );

    if (sortedQuestionnaires.length > 1) {
      const previousQuestionnaire = sortedQuestionnaires[1];
      const scoreKey = {
        General: "generalCapitalScore",
        Social: "socialCapitalScore",
        Physical: "physicalCapitalScore",
        Individual: "individualCapitalScore",
        Spiritual: "spiritualCapitalScore",
        Total: "recoveryCapitalTotal",
      }[label];

      setPrevScore(previousQuestionnaire[scoreKey]);
    }
  }, [allQuestionnaires, label]);

  const difference = prevScore !== null ? score - prevScore : null;

  return (
    <div
      className={`relative bg-gradient-to-b ${colorClass} backdrop-blur-xl rounded-xl border ${borderClass} p-4`}
    >
      {difference !== null && (
        <span
          className={`absolute top-2 right-2 text-sm ${
            difference > 0 ? "text-emerald-400" : "text-red-400"
          }`}
        >
          {difference > 0 ? "+" : ""}
          {difference}
        </span>
      )}
      <h3 className={`text-lg font-medium ${textClass} mb-2`}>{label}</h3>
      <p className="text-2xl font-bold text-white">{score ?? "-"}</p>
    </div>
  );
};

const ResidentProfile = () => {
  const { residentId } = useParams();
  const {
    organizationId,
    role,
    user,
    checkPermission,
    organizationData,
    checkFeature,
  } = useAuth();
  const navigate = useNavigate();
  const housesData = organizationData?.housesData || {};
  const residents = organizationData?.residents || [];
  const residentProfile = residents.find(
    (resident) => resident.id === residentId
  );
  const [formattedPhoneNumber, setFormattedPhoneNumber] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newReferral, setNewReferral] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isRecoveryInfoOpen, setIsRecoveryInfoOpen] = useState(false);
  const [isMedicationInfoOpen, setIsMedicationInfoOpen] = useState(false);
  const [isLegalInfoOpen, setIsLegalInfoOpen] = useState(false);
  const [availableSubstances, setAvailableSubstances] = useState({});
  const [viewingFormSubmission, setViewingFormSubmission] = useState(null);
  const [isEmergencyContactOpen, setIsEmergencyContactOpen] = useState(false);
  const [isHousingInfoOpen, setIsHousingInfoOpen] = useState(false);
  const [isQuestionnaireModalOpen, setQuestionnaireModalOpen] = useState(false);

  const [selectedQuestionnaireData, setSelectedQuestionnaireData] =
    useState(null);
  const [isResidentInfoOpen, setIsResidentInfoOpen] = useState(false);
  const [medicationSuggestions, setMedicationSuggestions] = useState([]);
  const [activeMedicationIndex, setActiveMedicationIndex] = useState(null);
  const [latestQuestionnaire, setLatestQuestionnaire] = useState(null);
  const [allQuestionnaires, setAllQuestionnaires] = useState([]);
  const [showMATStatusModal, setShowMATStatusModal] = useState(false);
  const [showProbationStatusModal, setShowProbationStatusModal] =
    useState(false);
  const [newMATDate, setNewMATDate] = useState("");
  const [newProbationDate, setNewProbationDate] = useState("");
  const [matNotes, setMATNotes] = useState("");
  const [probationNotes, setProbationNotes] = useState("");
  const [pendingMATChange, setPendingMATChange] = useState(null);
  const [pendingProbationChange, setPendingProbationChange] = useState(null);
  const [chartType, setChartType] = useState("domainScores");
  const [selectedDomain, setSelectedDomain] = useState("domainScores");
  const [averageScores, setAverageScores] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [paymentHistoryModalOpen, setPaymentHistoryModalOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [referralOptions, setReferralOptions] = useState([]);
  const [uaResultsModalOpen, setUaResultsModalOpen] = useState(false);
  const [uaResults, setUaResults] = useState([]);
  const [editingDoc, setEditingDoc] = useState(null);
  const [meetingRequirements, setMeetingRequirements] = useState(0);
  const [editedName, setEditedName] = useState("");
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState(false);
  const [residentNotes, setResidentNotes] = useState([]);
  const [showMoveInDateModal, setShowMoveInDateModal] = useState(false);
  const [newHouseMoveInDate, setNewHouseMoveInDate] = useState("");
  const [pendingHouseChange, setPendingHouseChange] = useState(null);
  const [dropdownItems, setDropdownItems] = useState([]);
  const [isLeaderViewingOwnProfile, setIsLeaderViewingOwnProfile] =
    useState(false);
  const [viewingResidentData, setViewingResidentData] = useState(null);
  const [showEmploymentModal, setShowEmploymentModal] = useState(false);
  const [employer, setEmployer] = useState("");
  const [employmentNotes, setEmploymentNotes] = useState("");
  const [newEmploymentDate, setNewEmploymentDate] = useState("");
  const [pendingWorkStatusChange, setPendingWorkStatusChange] = useState(null);
  const [phases, setPhases] = useState([]);
  const [organizationUsers, setOrganizationUsers] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [isFormCompletionModalOpen, setIsFormCompletionModalOpen] =
    useState(false);
  const [newNote, setNewNote] = useState({
    severity: "",
    subject: "",
    note: "",
    residentName: residentProfile?.fullName || "",
    date: new Date(),
    notifyUsers: [], // Add this to track selected users to notify
  });
  const [questionnaire, setQuestionnaire] = useState({
    residentId: residentId,
    saveAs: "",
  });

  const [editableProfile, setEditableProfile] = useState({
    house: "",
    rentSchedule: "",
    isMAT: false,
    referralLocation: "",
    medications: [],
  });
  const recoveryCapitalEnabled = checkFeature("recoveryCapital");

  const groupedUserOptions = useMemo(() => {
    const roleOptions = [
      { value: "All", label: "All Users" },
      { value: "Admin", label: "Admin" },
      { value: "Staff", label: "Staff" },
      { value: "Leader", label: "Leader" },
      { value: "Resident", label: "Resident" },
    ];

    const userOptions = organizationUsers.map((user) => ({
      value: user.id,
      label: `${user.firstName} ${user.lastName} (${user.role})`,
      isUser: true,
    }));

    return [
      { label: "Roles", options: roleOptions },
      { label: "Individual Users", options: userOptions },
    ];
  }, [organizationUsers]);

  // Add this with the other state declarations
  const currentHouseMoveInDate =
    editableProfile?.houseHistory?.length > 1
      ? editableProfile.houseHistory[editableProfile.houseHistory.length - 1]
          .moveInDate
      : null;

  // Permission checks
  const canAddForms = checkPermission("addForms");
  const canViewNotes = checkPermission("canViewNotes");
  const canAddNote = checkPermission("canAddNote");
  const canViewPaymentHistory = checkPermission("viewPaymentHistory");
  const canViewUaHistory = checkPermission("viewUaHistory");
  const canUpdateResidentInfo = checkPermission("updateResidentInfo");
  const canExportResidentData = checkPermission("exportResidentData");

  useEffect(() => {
    // For Leaders, check if their email matches the resident's email
    if (role === "Leader" && residentProfile) {
      // Compare the authenticated user's email with the resident's email
      const isViewingOwnProfile = user?.email === residentProfile.email;

      setIsLeaderViewingOwnProfile(isViewingOwnProfile);

      console.log("DEBUG: Leader role check:", {
        role,
        userEmail: user?.email,
        residentEmail: residentProfile?.email,
        residentId,
        isViewingOwnProfile,
      });
    } else {
      setIsLeaderViewingOwnProfile(false);
    }
  }, [role, user?.email, residentProfile, residentId]);

  useEffect(() => {
    const fetchMinimalData = async () => {
      // If Recovery Capital is disabled, load minimal data for the timeline
      if (!recoveryCapitalEnabled) {
        // Fetch limited payment history (last 5)
        if (paymentHistory.length === 0) {
          const paymentsQuery = query(
            collection(
              db,
              `organizations/${organizationId}/residents/${residentId}/payments`
            ),
            orderBy("paymentDate", "desc"),
            limit(5)
          );

          const querySnapshot = await getDocs(paymentsQuery);
          const paymentsData = querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
            paymentDate: doc.data().paymentDate?.toDate(),
          }));
          setPaymentHistory(paymentsData);
        }

        // Fetch limited UA results (last 5)
        if (uaResults.length === 0) {
          const uaResultsRef = collection(
            db,
            `organizations/${organizationId}/residents/${residentId}/uaResults`
          );

          const q = query(uaResultsRef, orderBy("date", "desc"), limit(5));
          const querySnapshot = await getDocs(q);

          const resultsData = querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));

          setUaResults(resultsData);
        }

        // Fetch limited document items (last 5)
        if (dropdownItems.length === 0) {
          // Fetch combined documents and file uploads
          const combinedItems = [];

          // Get the 5 most recent file uploads
          const fileUploadsRef = collection(
            db,
            `organizations/${organizationId}/residents/${residentId}/fileUploads`
          );
          const fileUploadsQuery = query(
            fileUploadsRef,
            orderBy("createdAt", "desc"),
            limit(5)
          );
          const fileUploadsSnapshot = await getDocs(fileUploadsQuery);

          fileUploadsSnapshot.docs.forEach((doc) => {
            combinedItems.push({
              id: doc.id,
              type: "fileUpload",
              ...doc.data(),
            });
          });

          // Get the 5 most recent form submissions
          const formSubmissionsRef = collection(
            db,
            `organizations/${organizationId}/residents/${residentId}/formSubmissions`
          );
          const formSubmissionsQuery = query(
            formSubmissionsRef,
            orderBy("dateSubmitted", "desc"),
            limit(5)
          );
          const formSubmissionsSnapshot = await getDocs(formSubmissionsQuery);

          formSubmissionsSnapshot.docs.forEach((doc) => {
            combinedItems.push({
              id: doc.id,
              type: "formSubmission",
              ...doc.data(),
            });
          });

          // Sort by date and limit to 5
          const sortedItems = combinedItems
            .sort((a, b) => {
              const dateA = a.createdAt || a.dateSubmitted || new Date();
              const dateB = b.createdAt || b.dateSubmitted || new Date();
              return new Date(dateB) - new Date(dateA);
            })
            .slice(0, 5);

          setDropdownItems(sortedItems);
        }
      }
    };

    // Run on component mount if Recovery Capital is disabled
    if (organizationId && residentId && !recoveryCapitalEnabled) {
      fetchMinimalData();
    }
  }, [
    organizationId,
    residentId,
    recoveryCapitalEnabled,
    paymentHistory.length,
    uaResults.length,
    dropdownItems.length,
  ]);

  const fetchDropdownItems = async () => {
    if (!organizationId || !residentId || !residentProfile?.fullName) return;

    try {
      // Show loading state if needed
      // setIsLoading(true);

      // Fetch file uploads from the resident's subcollection
      const fileUploadsRef = collection(
        db,
        `organizations/${organizationId}/residents/${residentId}/fileUploads`
      );
      const fileUploadsSnapshot = await getDocs(fileUploadsRef);
      const fileUploadsData = fileUploadsSnapshot.docs.map((doc) => ({
        id: doc.id,
        type: "fileUpload",
        ...doc.data(),
      }));

      // Fetch form submissions from resident's subcollection
      const formSubmissionsRef = collection(
        db,
        `organizations/${organizationId}/residents/${residentId}/formSubmissions`
      );
      const formSubmissionsSnapshot = await getDocs(formSubmissionsRef);
      const formSubmissionsData = formSubmissionsSnapshot.docs.map((f) => ({
        id: f.id,
        type: "formSubmission",
        ...f.data(),
      }));

      // Combine them if necessary
      const allItems = [...fileUploadsData, ...formSubmissionsData];
      setDropdownItems(allItems);

      // Hide loading state if needed
      // setIsLoading(false);
    } catch (error) {
      console.error("Error retrieving dropdown items:", error);
      // setIsLoading(false);
    }
  };

  useEffect(() => {
    if (residentProfile) {
      setEditableProfile({
        ...residentProfile,
        house: residentProfile.house || "",
        rentSchedule: residentProfile.rentSchedule || "",
        isMAT: residentProfile.isMAT ?? false,
        referralLocation: residentProfile.referralLocation || "",
        medications:
          residentProfile.medications?.filter(
            (med) => med.name.trim() !== "" || med.dose.trim() !== ""
          ) || [],
      });
    }
  }, [residentProfile]);

  // Create a function to fetch organization users
  const fetchUsers = async () => {
    if (!organizationId || organizationUsers.length > 0) return;

    try {
      const usersCollectionRef = collection(
        db,
        `organizations/${organizationId}/users`
      );
      const querySnapshot = await getDocs(usersCollectionRef);

      const usersData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setOrganizationUsers(usersData);
    } catch (error) {
      console.error("Error fetching organization users:", error);
    }
  };

  // Fetch phases if the phases feature is enabled
  const fetchPhases = async () => {
    if (phases.length > 0 || !organizationId || !checkFeature("phases")) return;

    try {
      const phasesRef = collection(
        db,
        `organizations/${organizationId}/phases`
      );
      const phaseSnapshot = await getDocs(phasesRef);

      if (!phaseSnapshot.empty) {
        const phaseData = phaseSnapshot.docs.map((doc) => ({
          id: doc.id,
          name: doc.data().name,
        }));
        setPhases(phaseData);
      }
    } catch (error) {
      console.error("Error loading phases:", error);
    }
  };

  const fetchUaResults = async () => {
    if (!organizationId || !residentId) return;

    try {
      // Query the UA results subcollection for this resident
      const uaResultsRef = collection(
        db,
        `organizations/${organizationId}/residents/${residentId}/uaResults`
      );

      const q = query(uaResultsRef, orderBy("date", "desc"));
      const querySnapshot = await getDocs(q);

      const resultsData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setUaResults(resultsData);
    } catch (error) {
      console.error("Error fetching UA results:", error);
    }
  };

  useEffect(() => {
    const getQuestionnaires = async () => {
      try {
        // Use the resident's subcollection instead of the central collection
        const questionnairesCollectionRef = collection(
          db,
          `organizations/${organizationId}/residents/${residentId}/recoveryCapital`
        );
        // No need to filter by resident name since we're using the resident's subcollection
        const querySnapshot = await getDocs(questionnairesCollectionRef);
        const questionnairesData = querySnapshot.docs.map((q) => ({
          id: q.id,
          ...q.data(),
        }));
        // Sort questionnaires by date and set them
        const sortedQuestionnaires = questionnairesData.sort((a, b) => {
          const dateA = a.timestamp?.seconds || 0;
          const dateB = b.timestamp?.seconds || 0;
          return dateA - dateB;
        });
        setLatestQuestionnaire(
          sortedQuestionnaires[sortedQuestionnaires.length - 1]
        );
        setAllQuestionnaires(sortedQuestionnaires);
      } catch (error) {
        console.error("Error retrieving questionnaires:", error);
      }
    };

    const fetchMinimalData = async () => {
      // If Recovery Capital is disabled, load minimal data for the timeline
      if (!recoveryCapitalEnabled) {
        // Fetch limited payment history (last 5)
        if (paymentHistory.length === 0) {
          const paymentsQuery = query(
            collection(
              db,
              `organizations/${organizationId}/residents/${residentId}/payments`
            ),
            orderBy("paymentDate", "desc"),
            limit(5)
          );

          const querySnapshot = await getDocs(paymentsQuery);
          const paymentsData = querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
            paymentDate: doc.data().paymentDate?.toDate(),
          }));
          setPaymentHistory(paymentsData);
        }

        // Fetch limited UA results (last 5)
        if (uaResults.length === 0) {
          const uaResultsRef = collection(
            db,
            `organizations/${organizationId}/residents/${residentId}/uaResults`
          );

          const q = query(uaResultsRef, orderBy("date", "desc"), limit(5));
          const querySnapshot = await getDocs(q);

          const resultsData = querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));

          setUaResults(resultsData);
        }

        // Fetch limited document items (last 5)
        if (dropdownItems.length === 0) {
          // Fetch combined documents and file uploads
          const combinedItems = [];

          // Get the 5 most recent file uploads
          const fileUploadsRef = collection(
            db,
            `organizations/${organizationId}/residents/${residentId}/fileUploads`
          );
          const fileUploadsQuery = query(
            fileUploadsRef,
            orderBy("createdAt", "desc"),
            limit(5)
          );
          const fileUploadsSnapshot = await getDocs(fileUploadsQuery);

          fileUploadsSnapshot.docs.forEach((doc) => {
            combinedItems.push({
              id: doc.id,
              type: "fileUpload",
              ...doc.data(),
            });
          });

          // Get the 5 most recent form submissions
          const formSubmissionsRef = collection(
            db,
            `organizations/${organizationId}/residents/${residentId}/formSubmissions`
          );
          const formSubmissionsQuery = query(
            formSubmissionsRef,
            orderBy("dateSubmitted", "desc"),
            limit(5)
          );
          const formSubmissionsSnapshot = await getDocs(formSubmissionsQuery);

          formSubmissionsSnapshot.docs.forEach((doc) => {
            combinedItems.push({
              id: doc.id,
              type: "formSubmission",
              ...doc.data(),
            });
          });

          // Sort by date and limit to 5
          const sortedItems = combinedItems
            .sort((a, b) => {
              const dateA = a.createdAt || a.dateSubmitted || new Date();
              const dateB = b.createdAt || b.dateSubmitted || new Date();
              return new Date(dateB) - new Date(dateA);
            })
            .slice(0, 5);

          setDropdownItems(sortedItems);
        }
      }
    };

    // Only fetch questionnaires data if the RecoveryCapital feature is enabled
    if (organizationId && residentId && recoveryCapitalEnabled) {
      getQuestionnaires();
    } else if (organizationId && residentId && !recoveryCapitalEnabled) {
      // Run on component mount if Recovery Capital is disabled
      fetchMinimalData();
    }
  }, [
    organizationId,
    residentId,
    recoveryCapitalEnabled,
    paymentHistory.length,
    uaResults.length,
    dropdownItems.length,
  ]);

  const fetchReferralOptions = async () => {
    if (!organizationId) return;

    try {
      const settingsRef = doc(
        db,
        `organizations/${organizationId}/houseSettings/initial`
      );
      const settingsSnap = await getDoc(settingsRef);

      if (settingsSnap.exists() && settingsSnap.data().referrals) {
        setReferralOptions(settingsSnap.data().referrals);
      }
    } catch (error) {
      console.error("Error loading referral options:", error);
    }
  };

  // Then call this in a useEffect
  useEffect(() => {
    fetchReferralOptions();
  }, [organizationId]);

  const fetchPaymentHistory = async () => {
    if (!organizationId || !residentId) return;

    try {
      // Updated path to fetch directly from resident's payments subcollection
      const paymentsQuery = query(
        collection(
          db,
          `organizations/${organizationId}/residents/${residentId}/payments`
        )
      );
      const querySnapshot = await getDocs(paymentsQuery);
      const paymentsData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        paymentDate: doc.data().paymentDate?.toDate(), // Converting timestamp to Date if exists
      }));
      setPaymentHistory(paymentsData);
    } catch (error) {
      console.error("Error fetching payment history:", error);
    }
  };

  const fetchNotes = async () => {
    if (!organizationId || !residentId) return;

    try {
      // Use the resident subcollection instead of the general collection
      const notesCollectionRef = collection(
        db,
        `organizations/${organizationId}/residents/${residentId}/notes`
      );
      const querySnapshot = await getDocs(notesCollectionRef);

      const notesData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date?.toDate(),
      }));

      // Sort by date descending
      setResidentNotes(notesData.sort((a, b) => b.date - a.date));
    } catch (error) {
      console.error("Error fetching notes:", error);
    }
  };

  useEffect(() => {
    const fetchMeetingRequirements = async () => {
      try {
        const residentDocRef = doc(
          db,
          `organizations/${organizationId}/residents`,
          residentId
        );
        const residentDocSnap = await getDoc(residentDocRef);

        if (residentDocSnap.exists()) {
          const meetingReq = residentDocSnap.data().meetingRequirements;
          setMeetingRequirements(meetingReq ?? 0); // Use nullish coalescing
        }
      } catch (error) {
        console.error("Error fetching meeting requirements:", error);
      }
    };

    if (residentId && organizationId) {
      fetchMeetingRequirements();
    }
  }, [residentId, organizationId]);

  const handleMATStatusChange = (newMATStatus) => {
    const currentStatus = editableProfile.isMAT;

    // If changing from true to false (getting off MAT)
    if (currentStatus === true && newMATStatus === false) {
      setPendingMATChange(false);
      setShowMATStatusModal(true);
      setNewMATDate(new Date().toISOString().split("T")[0]); // Default to today
    } else {
      // Direct update for other changes
      handleChange("isMAT", newMATStatus);
    }
  };

  const handlePOFieldChange = (field, value) => {
    // Update the field first
    handleChange(field, value);

    // Check if both PO fields will be empty after this change
    const updatedProfile = {
      ...editableProfile,
      [field]: value,
    };

    const hadPOInfo = editableProfile.poFirstName || editableProfile.poLastName;
    const willHavePOInfo =
      updatedProfile.poFirstName || updatedProfile.poLastName;

    // If they had PO info before but won't after this change (getting off probation)
    if (hadPOInfo && !willHavePOInfo) {
      setPendingProbationChange(true);
      setShowProbationStatusModal(true);
      setNewProbationDate(new Date().toISOString().split("T")[0]); // Default to today
    }
  };

  // Cancel MAT status change
  const cancelMATStatusChange = () => {
    setShowMATStatusModal(false);
    setPendingMATChange(null);
    setNewMATDate("");
    setMATNotes("");
  };

  // Confirm MAT status change and update history
  const confirmMATStatusChange = async () => {
    if (!newMATDate) return;

    try {
      // Create new MAT history record
      const newRecord = {
        date: newMATDate,
        status: pendingMATChange ? "Started MAT" : "Ended MAT",
        notes: matNotes || null,
      };

      // Update MAT history array
      const updatedHistory = [...(editableProfile.matHistory || []), newRecord];

      // Sort by date (newest first)
      updatedHistory.sort((a, b) => new Date(b.date) - new Date(a.date));

      // Update Firestore
      const residentRef = doc(
        db,
        `organizations/${organizationId}/residents`,
        residentId
      );

      await updateDoc(residentRef, {
        isMAT: pendingMATChange,
        matHistory: updatedHistory,
      });

      // Update local state
      setEditableProfile((prev) => ({
        ...prev,
        isMAT: pendingMATChange,
        matHistory: updatedHistory,
      }));

      // Close modal and reset
      setShowMATStatusModal(false);
      setPendingMATChange(null);
      setNewMATDate("");
      setMATNotes("");
    } catch (error) {
      console.error("Error updating MAT status:", error);
    }
  };

  // Cancel probation status change
  const cancelProbationStatusChange = () => {
    setShowProbationStatusModal(false);
    setPendingProbationChange(null);
    setNewProbationDate("");
    setProbationNotes("");

    // Restore the PO fields to their previous values
    // This is a bit tricky since we already updated them
    // You might want to keep track of previous values
  };

  // Confirm probation status change and update history
  const confirmProbationStatusChange = async () => {
    if (!newProbationDate) return;

    try {
      // Create new probation history record
      const newRecord = {
        date: newProbationDate,
        status: pendingProbationChange
          ? "Started Probation/Parole"
          : "Ended Probation/Parole",
        notes: probationNotes || null,
      };

      // Update probation history array
      const updatedHistory = [
        ...(editableProfile.probationHistory || []),
        newRecord,
      ];

      // Sort by date (newest first)
      updatedHistory.sort((a, b) => new Date(b.date) - new Date(a.date));

      // Update Firestore
      const residentRef = doc(
        db,
        `organizations/${organizationId}/residents`,
        residentId
      );

      await updateDoc(residentRef, {
        probationHistory: updatedHistory,
      });

      // Update local state
      setEditableProfile((prev) => ({
        ...prev,
        probationHistory: updatedHistory,
      }));

      // Close modal and reset
      setShowProbationStatusModal(false);
      setPendingProbationChange(null);
      setNewProbationDate("");
      setProbationNotes("");
    } catch (error) {
      console.error("Error updating probation status:", error);
    }
  };

  const handleAddReferralOption = async (newReferral) => {
    if (!newReferral || !organizationId) return;

    try {
      // Get reference to the house settings document
      const settingsRef = doc(
        db,
        `organizations/${organizationId}/houseSettings/initial`
      );

      // Use arrayUnion to add the new referral to the list without duplicates
      await updateDoc(settingsRef, {
        referrals: arrayUnion(newReferral),
      });

      // Update local state
      // This assumes you have a referralOptions state like in IntakeForm
      // If not, add: const [referralOptions, setReferralOptions] = useState([]);
      const updatedSnap = await getDoc(settingsRef);
      if (updatedSnap.exists()) {
        setReferralOptions(updatedSnap.data().referrals);

        // Update the resident's referral location
        setEditableProfile((prevState) => ({
          ...prevState,
          referralLocation: newReferral,
        }));
      }

      console.log("Successfully added new referral option:", newReferral);
    } catch (error) {
      console.error("Error adding referral option:", error);
    }
  };

  const handleWorkStatusChange = (newWorkStatus) => {
    const currentStatus = editableProfile.workStatus;

    // If there's any change in status and it's not just initialization to empty
    if (currentStatus !== newWorkStatus && newWorkStatus !== "") {
      setPendingWorkStatusChange(newWorkStatus);
      setShowEmploymentModal(true);
      setNewEmploymentDate(new Date().toISOString().split("T")[0]); // Default to today
    } else {
      // If no significant change or just initialization, update directly
      handleChange("workStatus", newWorkStatus);
    }
  };

  // Cancel the work status change
  const cancelWorkStatusChange = () => {
    setShowEmploymentModal(false);
    setPendingWorkStatusChange(null);
    setNewEmploymentDate("");
    setEmployer("");
    setEmploymentNotes("");
  };

  // Confirm the work status change and update employment history
  const confirmWorkStatusChange = async () => {
    if (!newEmploymentDate) return;

    try {
      // Create new employment history record
      const newRecord = {
        date: newEmploymentDate,
        status: pendingWorkStatusChange,
        employer: employer || null,
        notes: employmentNotes || null,
      };

      // Update employment history array
      const updatedHistory = [
        ...(editableProfile.employmentHistory || []),
        newRecord,
      ];

      // Sort by date (newest first)
      updatedHistory.sort((a, b) => new Date(b.date) - new Date(a.date));

      // Update Firestore
      const residentRef = doc(
        db,
        `organizations/${organizationId}/residents`,
        residentId
      );

      await updateDoc(residentRef, {
        workStatus: pendingWorkStatusChange,
        employmentHistory: updatedHistory,
      });

      // Update local state
      setEditableProfile((prev) => ({
        ...prev,
        workStatus: pendingWorkStatusChange,
        employmentHistory: updatedHistory,
      }));

      // Close modal and reset
      setShowEmploymentModal(false);
      setPendingWorkStatusChange(null);
      setNewEmploymentDate("");
      setEmployer("");
      setEmploymentNotes("");
    } catch (error) {
      console.error("Error updating employment status:", error);
      // You might want to show an error message to the user here
    }
  };

  const handleMedicationNameInput = (e, index) => {
    const value = e.target.value;
    handleMedicationChange(index, "name", value); // existing updater

    if (value.trim() === "") {
      setMedicationSuggestions([]);
      setActiveMedicationIndex(null);
      return;
    }

    setMedicationSuggestions(findMedicationSuggestions(value));
    setActiveMedicationIndex(index);
  };

  const handleSelectSuggestion = (suggestion, index) => {
    // store as “Brand (Generic)” – same format the IntakeForm uses
    const fullName = `${suggestion.brandName} (${suggestion.genericName})`;

    handleMedicationChange(index, "name", fullName); // <-- save formatted text
    setMedicationSuggestions([]);
    setActiveMedicationIndex(null);
  };

  const handleSubmitNote = async () => {
    try {
      // Handle special case: "All" users notification
      let notificationRecipients = [];
      if (newNote.notifyUsers && newNote.notifyUsers.length > 0) {
        notificationRecipients = newNote.notifyUsers.map((option) => ({
          id: option.value,
          isRole: !option.isUser,
        }));
      }

      // Create note data
      const noteData = {
        ...newNote,
        date: new Date(),
        organizationId,
        // Store notification recipients in simplified format for Firestore
        notifyUsers:
          notificationRecipients.length > 0
            ? notificationRecipients.map((r) => ({
                id: r.id,
                isRole: r.isRole,
              }))
            : null,
      };

      // Use the resident subcollection instead of the general collection
      const notesCollectionRef = collection(
        db,
        `organizations/${organizationId}/residents/${residentId}/notes`
      );

      const noteRef = await addDoc(notesCollectionRef, noteData);

      // If there are notification recipients, create notifications
      if (notificationRecipients.length > 0) {
        // Check if "All" users is selected
        const notifyAll = notificationRecipients.some(
          (r) => r.isRole && r.id === "All"
        );

        if (notifyAll) {
          // Notify all users in the organization
          const usersSnapshot = await getDocs(
            collection(db, `organizations/${organizationId}/users`)
          );

          const allUserIds = usersSnapshot.docs.map((doc) => doc.id);

          // Create notifications for all users
          for (const userId of allUserIds) {
            await createUserNotification(
              userId,
              `Note about ${newNote.residentName}`,
              newNote.subject,
              noteRef.id,
              newNote.note
            );
          }
        } else {
          // Send notifications to selected recipients
          for (const recipient of notificationRecipients) {
            if (recipient.isRole) {
              // Handle role-based notifications
              await createRoleNotification(
                recipient.id,
                `Note about ${newNote.residentName}`,
                newNote.subject,
                noteRef.id,
                newNote.note
              );
            } else {
              // Handle individual user notifications
              await createUserNotification(
                recipient.id,
                `Note about ${newNote.residentName}`,
                newNote.subject,
                noteRef.id,
                newNote.note
              );
            }
          }
        }
      }

      // Reset form and close modal
      setNewNote({
        severity: "",
        subject: "",
        note: "",
        residentName: residentProfile?.fullName || "",
        date: new Date(),
        notifyUsers: [],
      });
      setIsAddNoteModalOpen(false);

      // Refresh notes list
      const querySnapshot = await getDocs(notesCollectionRef);
      const notesData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date?.toDate(),
      }));

      setResidentNotes(notesData.sort((a, b) => b.date - a.date));
    } catch (error) {
      console.error("Error adding note:", error);
    }
  };

  const createUserNotification = async (
    userId,
    title,
    message,
    noteId,
    noteContent
  ) => {
    try {
      const notificationData = {
        title,
        message, // This is the subject
        content: noteContent, // This is the full note text
        createdAt: new Date(),
        read: false,
        type: "note",
        noteId,
        residentId,
        sourceType: "residentNotes", // Use the same ID as in NOTIFICATION_TYPES
        forceEmail: false, // Allow the email function to check user preferences
      };

      // Use the user's notifications subcollection
      const notificationPath = `organizations/${organizationId}/users/${userId}/notifications`;
      await addDoc(collection(db, notificationPath), notificationData);
    } catch (error) {
      console.error("Error creating user notification:", error);
    }
  };

  const createRoleNotification = async (
    role,
    title,
    message,
    noteId,
    noteContent
  ) => {
    try {
      // Get all users with this role
      const usersCollectionRef = collection(
        db,
        `organizations/${organizationId}/users`
      );
      const q = query(usersCollectionRef, where("role", "==", role));
      const querySnapshot = await getDocs(q);

      // Process each user with the specified role
      const notificationPromises = querySnapshot.docs.map((userDoc) => {
        const userId = userDoc.id;
        const notificationData = {
          title,
          message, // This is the subject
          content: noteContent, // This is the full note text
          createdAt: new Date(),
          read: false,
          type: "note",
          noteId,
          residentId,
          sourceType: "residentNotes", // Use the same ID as in NOTIFICATION_TYPES
          forceEmail: false, // Allow the email function to check user preferences
        };

        // Use the user's notifications subcollection
        const notificationPath = `organizations/${organizationId}/users/${userId}/notifications`;
        return addDoc(collection(db, notificationPath), notificationData);
      });

      // Wait for all notifications to be created
      await Promise.all(notificationPromises);
    } catch (error) {
      console.error("Error creating role notification:", error);
    }
  };

  const domainQuestions = {
    General: [
      "criminalJustice",
      "employed",
      "sponsor",
      "lastStepCompleted",
      "totalRecoveryTime",
      "educationLevel",
      "lifeSatisfaction",
      "jailExperienceEffect",
      "adverseExperiences",
      "therapy",
      "reliableTransportation", // Add this if missing
    ],
    Social: [
      "supportOthers",
      "friendsInRecovery",
      "authenticSelf", // Make sure this matches your actual question ID
      "feelOutcast",
      "healthySocialGroup",
      "rebuiltRelationships",
      "strongRelationKids", // These are conditional - make sure they exist
      "strongRelationFamily",
      "familyDysfunction",
      "healthyBoundaries", // Add this if missing
      "partOfCommunity",
    ],
    Physical: [
      "bodyImage",
      "enoughMoney",
      "pain",
      "motivation",
      "medicalInsurance",
      "generalHygeine", // Check spelling - might be "generalHygiene"
      "workoutRegularly",
      "eatHealthy",
      "sleep",
      "physicallyHealthy",
    ],
    Individual: [
      "hardToHaveFun",
      "guiltOrShame",
      "decisionMakingAbilities",
      "selfImprovement",
      "mentalHealthProblems",
      "lifeHasPurpose",
      "hardToTrust",
      "cravings", // ← Fixed: was "maintainRecovery"
      "emotionalRegulation",
      "hardToLetGo",
    ],
    Spiritual: [
      "spirituallyConnected",
      "spiritualGrowth",
      "innerPeace",
      "forgiveness",
      "spiritualPractices",
      "greaterPurpose",
      "deepGratitude",
      "selfReflection",
      "moralConviction",
      "happyFuture", // Add this if missing
    ],
  };

  const negativeQuestions = [
    "feelOutcast",
    "familyDysfunction",
    "pain",
    "financialStress",
    "criminalJustice",
    "hardToHaveFun",
    "guiltOrShame",
    "mentalHealthProblems",
    "hardToLetGo",
  ];

  const generalCapitalQuestions = [
    { text: "I speak to my sponsor 4+ times a week", id: "sponsor" },
    { text: "I am currently employed.", id: "employed" },
    {
      text: "I regularly attend therapy sessions with a licensed professional.",
      id: "therapy",
    },
    {
      text: "The criminal justice system (e.g., probation/parole, pending cases) impacts my recovery negatively.",
      id: "criminalJustice",
    },
    { text: "Do you have kids?", id: "kids" },
  ];

  const socialCapitalQuestions = [
    {
      text: "I actively support other people who are in recovery",
      id: "supportOthers",
    },
    {
      text: "I have friends in recovery who I can turn to for advice and support",
      id: "friendsInRecovery",
    },
    { text: "My family supports me in my recovery", id: "familySupport" },
    { text: "I feel like an outcast", id: "feelOutcast" },
    {
      text: "I am never around people who use drugs and/or alcohol",
      id: "healthySocialGroup",
    },
    {
      text: "I have rebuilt important relationships that were damaged due to addiction",
      id: "rebuiltRelationships",
    },
    {
      text: "My family engages in behaviors that hinder my recovery",
      id: "familyDysfunction",
    },
    {
      text: "I am comfortable setting and maintaining healthy boundaries in my relationships",
      id: "healthyBoundaries",
    },
    {
      text: "I feel like I'm part of a recovery community",
      id: "partOfCommunity",
    },
  ];

  const physicalCapitalQuestions = [
    { text: "I feel comfortable with my body image.", id: "bodyImage" },
    {
      text: "I have enough money every week to buy the basic things I need",
      id: "enoughMoney",
    },
    { text: "I am regularly in physical pain", id: "pain" },
    { text: "I feel motivated most days", id: "motivation" },
    {
      text: "I have medical insurance that covers my medical needs",
      id: "medicalInsurance",
    },
    { text: "I have a lot of financial stress", id: "financialStress" },
    { text: "I work out 3-5 times a week", id: "workoutRegularly" },
    { text: "I eat generally healthy", id: "eatHealthy" },
    { text: "I sleep for 7-9 hours a night", id: "sleep" },
    { text: "I feel physically healthy most days", id: "physicallyHealthy" },
  ];

  const individualCapitalQuestions = [
    { text: "I find it hard to have fun", id: "hardToHaveFun" },
    { text: "I struggle with guilt or shame", id: "guiltOrShame" },
    {
      text: "I am confident in my decision-making abilities",
      id: "decisionMakingAbilities",
    },
    {
      text: "I actively seek out resources or knowledge to improve myself.",
      id: "selfImprovement",
    },
    {
      text: "I have problems with my mental health",
      id: "mentalHealthProblems",
    },
    { text: "I feel my life has purpose and meaning", id: "lifeHasPurpose" },
    { text: "It's hard for me to trust others", id: "hardToTrust" },
    {
      text: "I have no desire to use drugs or alcohol again, now or in the future",
      id: "desireToUse",
    },
    {
      text: "I am good at regulating myself emotionally",
      id: "emotionalRegulation",
    },
    {
      text: "It's hard to let go of the part of my identity that was linked to my drinking or drug use",
      id: "hardToLetGo",
    },
  ];

  const spiritualCapitalQuestions = [
    { text: "I feel spiritually connected", id: "spirituallyConnected" },
    {
      text: "My spiritual growth is important to my overall well-being",
      id: "spiritualGrowth",
    },
    { text: "I feel a deep sense of inner peace", id: "innerPeace" },
    {
      text: "I believe forgiveness is necessary in my healing journey",
      id: "forgiveness",
    },
    {
      text: "I regularly engage in spiritual practices (e.g., prayer, meditation)",
      id: "spiritualPractices",
    },
    {
      text: "I believe my recovery has a greater purpose beyond myself",
      id: "greaterPurpose",
    },
    {
      text: "I experience moments of deep gratitude in my journey",
      id: "deepGratitude",
    },
    {
      text: "I take time for self-reflection regularly.",
      id: "selfReflection",
    },
    {
      text: "I regularly follow and adhere to my moral and ethical compass",
      id: "moralConviction",
    },
    {
      text: "I believe I will have a bright, happy, and healthy future",
      id: "happyFuture",
    },
  ];

  const allQuestions = [
    ...generalCapitalQuestions,
    ...socialCapitalQuestions,
    ...physicalCapitalQuestions,
    ...individualCapitalQuestions,
    ...spiritualCapitalQuestions,
  ];

  const exportToCSV = () => {
    if (allQuestionnaires.length === 0) return;

    const headers = [
      "Questionnaire",
      "Date",
      "General",
      "Social",
      "Physical",
      "Individual",
      "Spiritual",
      "Total",
      ...allQuestions.map((q) => q.id),
    ];

    const dataRows = allQuestionnaires.map((q) => [
      q.questionnaire,
      q.timestamp
        ? new Date(q.timestamp.seconds * 1000).toLocaleDateString()
        : "Invalid Date",
      q.generalCapitalScore,
      q.socialCapitalScore,
      q.physicalCapitalScore,
      q.individualCapitalScore,
      q.spiritualCapitalScore,
      q.recoveryCapitalTotal,
      ...allQuestions.map((question) => q[question.id] || ""),
    ]);

    const questionKey = allQuestions
      .map((q) => `${q.id},${q.text.replace(/,/g, ";")}`)
      .join("\n");

    const csvContent = [
      headers.join(","),
      ...dataRows.map((row) => row.join(",")),
      "\nQuestion Key:",
      "ID,Question",
      questionKey,
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `recovery_capital_data_${residentProfile.firstName}_${residentProfile.lastName}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const navigateToResidentDashboard = () => {
    console.log("DEBUG: Navigate to dashboard clicked", {
      residentId,
      residentName: `${residentProfile?.firstName} ${residentProfile?.lastName}`,
      userEmail: user?.email,
      isLeaderViewingOwnProfile,
    });

    // Store the residentId in sessionStorage
    sessionStorage.setItem("viewingResidentId", residentId);
    sessionStorage.setItem(
      "viewingResidentName",
      `${residentProfile?.firstName} ${residentProfile?.lastName}`
    );

    // Navigate to the ResidentDashboard route
    navigate("/residentdashboard");
  };

  useEffect(() => {
    const fetchResidentData = async () => {
      const residentId = sessionStorage.getItem("viewingResidentId");
      if (!residentId) return;

      try {
        const residentDoc = await getDoc(
          doc(db, `organizations/${organizationId}/residents`, residentId)
        );
        if (residentDoc.exists()) {
          setViewingResidentData({
            id: residentDoc.id,
            ...residentDoc.data(),
          });
        }
      } catch (error) {
        console.error("Error fetching resident data:", error);
      }
    };

    fetchResidentData();
  }, [organizationId]);

  const fetchAverageScores = async (questionnaireType) => {
    if (!organizationId) return;

    try {
      let totals = {};
      let count = 0;

      // Reference to the central recoveryCapital collection
      const recoveryCapitalCentralRef = collection(
        db,
        `organizations/${organizationId}/recoveryCapital`
      );

      // Query for documents with the specified questionnaire type
      const q = query(
        recoveryCapitalCentralRef,
        where("questionnaire", "==", questionnaireType)
      );

      const querySnapshot = await getDocs(q);

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Loop through each key-value pair in the document
        for (const [key, value] of Object.entries(data)) {
          // Convert to numeric value if possible
          const numericValue = parseFloat(value);
          // Only sum values that are numeric and not in the exclusion list
          if (
            !isNaN(numericValue) &&
            key !== "residentName" &&
            key !== "kids" &&
            key !== "staffNotes" &&
            key !== "timestamp"
          ) {
            totals[key] = (totals[key] || 0) + numericValue;
          }
        }
        count++;
      });

      // Calculate averages by dividing totals by count
      const averages = Object.fromEntries(
        Object.entries(totals).map(([key, total]) => [key, total / count])
      );

      setAverageScores(averages);
    } catch (error) {
      console.error("Error calculating averages:", error);
    }
  };

  const handleUpdate = async () => {
    if (!organizationId) return;

    try {
      const cleanedProfile = {
        ...editableProfile,
        phoneNumber: editableProfile.phoneNumber || "", // Ensure this field is saved
        ecPhoneNumber: editableProfile.ecPhoneNumber || "", // Ensure this field is saved
        medications: editableProfile.medications.filter(
          (med) => med.name.trim() !== "" || med.dose.trim() !== ""
        ),
      };

      const userProfileRef = doc(
        db,
        `organizations/${organizationId}/residents`,
        residentId
      );
      await updateDoc(userProfileRef, cleanedProfile);
      fetchResidents(); // Refresh the data if needed
    } catch (err) {
      console.error(err);
    }
  };

  const handleHouseChange = (newHouse) => {
    if (editableProfile.house === newHouse) return;
    setPendingHouseChange(newHouse);
    setShowMoveInDateModal(true);
  };

  const confirmHouseChange = async () => {
    if (!pendingHouseChange) return;

    try {
      const currentHouse = editableProfile.house;
      const houseHistory = editableProfile.houseHistory || [];
      const currentMoveInDate =
        houseHistory.length > 0
          ? houseHistory[houseHistory.length - 1].moveInDate
          : editableProfile.intakeDate;

      const updates = {
        house: pendingHouseChange,
        houseHistory: [
          ...houseHistory,
          {
            house: currentHouse,
            moveInDate: currentMoveInDate,
            moveOutDate: newHouseMoveInDate,
          },
          {
            house: pendingHouseChange,
            moveInDate: newHouseMoveInDate,
            moveOutDate: null,
          },
        ],
      };

      // Update Firestore
      const residentRef = doc(
        db,
        `organizations/${organizationId}/residents`,
        residentId
      );
      await updateDoc(residentRef, updates);

      // Update local state
      setEditableProfile((prev) => ({
        ...prev,
        ...updates,
      }));

      // No need for fetchResidents here
    } catch (error) {
      console.error("Error updating resident house:", error);
    } finally {
      setShowMoveInDateModal(false);
      setPendingHouseChange(null);
      setNewHouseMoveInDate("");
    }
  };

  const cancelHouseChange = () => {
    setEditableProfile((prev) => ({ ...prev, house: prev.house }));
    setShowMoveInDateModal(false);
    setPendingHouseChange(null);
    setNewHouseMoveInDate("");
  };

  const handleChange = (key, value) => {
    setEditableProfile((prev) => {
      // Create the updated profile
      const updatedProfile = {
        ...prev,
        [key]: value,
      };

      // Update fullName if first or last name changes
      if (key === "firstName" || key === "lastName") {
        updatedProfile.fullName = `${
          key === "firstName" ? value : prev.firstName
        } ${key === "lastName" ? value : prev.lastName}`;
      }

      return updatedProfile;
    });

    // Immediately update Firebase if dealing with houseHistory
    if (key === "houseHistory") {
      const userProfileRef = doc(
        db,
        `organizations/${organizationId}/residents`,
        residentId
      );
      updateDoc(userProfileRef, { [key]: value }).catch((err) => {
        console.error("Error updating Firebase:", err);
      });
    }
  };

  const handleMedicationChange = (index, field, value) => {
    setEditableProfile((prev) => {
      const updatedMedications = [...prev.medications];
      updatedMedications[index] = {
        ...updatedMedications[index],
        [field]: value,
      };

      // Filter out medications with both fields empty
      const cleanedMedications = updatedMedications.filter(
        (med) => med.name.trim() !== "" || med.dose.trim() !== ""
      );

      return {
        ...prev,
        medications: cleanedMedications,
      };
    });
  };

  const addMedication = () => {
    setEditableProfile((prevProfile) => ({
      ...prevProfile,
      medications: [...(prevProfile.medications || []), { name: "", dose: "" }],
    }));
  };

  const handleFileChange = (event) => {
    const newFiles = Array.from(event.target.files);
    setSelectedFiles((prev) => [...prev, ...newFiles]);
    // Don't set isModalOpen here anymore since we want to allow multiple selections
  };

  const removeFile = (indexToRemove) => {
    setSelectedFiles((prev) =>
      prev.filter((_, index) => index !== indexToRemove)
    );
  };

  const handleQuestionnaireClick = (id) => {
    const questionnaire = allQuestionnaires.find((q) => q.id === id);
    setSelectedQuestionnaireData(questionnaire);
    fetchAverageScores(questionnaire.questionnaire);
    setQuestionnaireModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (!organizationId || !selectedFiles.length || !questionnaire.saveAs) {
        console.error("Missing organization ID, files, or saveAs group name.");
        return;
      }

      const uploadPromises = selectedFiles.map(async (file) => {
        // OPTIONAL: change the storage path to reflect the nested structure:
        // e.g., `organizations/${organizationId}/residents/${residentId}/fileUploads/${file.name}`
        const filePath = `organizations/${organizationId}/residents/${residentId}/fileUploads/${file.name}`;
        const storageRef = ref(storage, filePath);

        await uploadBytes(storageRef, file);
        const fileURL = await getDownloadURL(storageRef);

        const formData = {
          organizationId,
          residentId,
          saveAs: questionnaire.saveAs,
          fileName: file.name,
          fileURL,
          createdAt: new Date(),
        };

        // Use subcollection under the resident:
        const fileUploadsCollectionRef = collection(
          db,
          `organizations/${organizationId}/residents/${residentId}/fileUploads`
        );

        const docRef = await addDoc(fileUploadsCollectionRef, formData);

        // Return document metadata with the generated ID
        return { id: docRef.id, ...formData };
      });

      const newDocuments = await Promise.all(uploadPromises);

      // Update the documents (i.e., fileUploads) state with newly uploaded docs
      setDocuments((prevDocs) => [...prevDocs, ...newDocuments]);

      // Cleanup
      setIsModalOpen(false);
      setQuestionnaire({ residentId, saveAs: "" });
      setSelectedFiles([]);
      console.log("File uploads saved successfully");
    } catch (error) {
      console.error("Error uploading files:", error);
    }
  };

  // Add this inversion function at the top of your PDF generation file
  const invertWhiteToBlack = async (imageUrl) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";

      img.onload = () => {
        // Create a canvas to process the image
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");

        // Draw the image
        ctx.drawImage(img, 0, 0);

        // Always invert for signatures and light images
        ctx.globalCompositeOperation = "difference";
        ctx.fillStyle = "white";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        resolve(canvas.toDataURL("image/png"));
      };

      img.onerror = () => {
        console.error("Error loading image for inversion");
        resolve(imageUrl); // Return original on error
      };

      img.src = imageUrl;
    });
  };

  const generateFormPDF = async (formSubmission, organizationData = {}) => {
    // Create new jsPDF document
    const doc = new jsPDF();
    let yPosition = 10;
    const leftMargin = 14;
    const pageWidth = doc.internal.pageSize.width;
    const rightMargin = pageWidth - leftMargin;

    // ===== ORGANIZATION HEADER SECTION =====
    const { houseSettings = {} } = organizationData;
    let { headerLogo, headerTitle, headerContact } = houseSettings;

    // Check if there's a headerImage from form submission
    if (formSubmission.responses && formSubmission.responses.elementMetadata) {
      const headerImageKey = Object.keys(
        formSubmission.responses.elementMetadata
      ).find(
        (key) =>
          formSubmission.responses.elementMetadata[key].type === "headerImage"
      );

      if (headerImageKey && formSubmission.responses.responses) {
        const headerImageData =
          formSubmission.responses.responses[headerImageKey];
        if (
          headerImageData &&
          typeof headerImageData === "object" &&
          headerImageData.imageUrl
        ) {
          headerLogo = headerImageData.imageUrl;
        } else if (typeof headerImageData === "string") {
          headerLogo = headerImageData;
        }
      }
    }

    // Add organization header
    if (headerLogo || headerTitle || headerContact) {
      // Add logo if exists
      if (headerLogo) {
        try {
          const processedLogo = await invertWhiteToBlack(headerLogo);
          const logoWidth = pageWidth - leftMargin * 2;
          const logoHeight = 30;
          doc.addImage(
            processedLogo,
            "PNG",
            leftMargin,
            yPosition,
            logoWidth,
            logoHeight
          );
          yPosition += logoHeight + 8;
        } catch (error) {
          console.error("Error adding logo to PDF:", error);
        }
      }

      // Add organization name or fallback
      if (headerTitle) {
        doc.setFontSize(18);
        doc.setFont(undefined, "bold");
        doc.text(headerTitle, pageWidth / 2, yPosition, { align: "center" });
        yPosition += 10;
      } else {
        doc.setFontSize(18);
        doc.setFont(undefined, "bold");
        doc.text("Organization Form", pageWidth / 2, yPosition, {
          align: "center",
        });
        yPosition += 10;
      }

      // Add contact info if exists
      if (headerContact) {
        doc.setFontSize(10);
        doc.setFont(undefined, "normal");
        const contactLines = headerContact.split("\n");
        contactLines.forEach((line) => {
          doc.text(line, pageWidth / 2, yPosition, { align: "center" });
          yPosition += 5;
        });
        yPosition += 5;
      }

      // Divider
      doc.setLineWidth(0.5);
      doc.line(leftMargin, yPosition, rightMargin, yPosition);
      yPosition += 10;
    } else {
      // Default header
      doc.setFontSize(18);
      doc.setFont(undefined, "bold");
      doc.text("Form Submission", pageWidth / 2, yPosition, {
        align: "center",
      });
      yPosition += 15;

      doc.setLineWidth(0.5);
      doc.line(leftMargin, yPosition, rightMargin, yPosition);
      yPosition += 10;
    }

    // ===== FORM TITLE =====
    doc.setFontSize(16);
    doc.setFont(undefined, "bold");
    doc.text(
      formSubmission.formName || "Form Submission",
      pageWidth / 2,
      yPosition,
      { align: "center" }
    );
    yPosition += 15;

    // ===== METADATA SECTION =====
    doc.setFontSize(10);
    doc.setFont(undefined, "normal");

    const submittedDate =
      formSubmission.dateSubmitted &&
      typeof formSubmission.dateSubmitted === "object" &&
      "seconds" in formSubmission.dateSubmitted
        ? new Date(formSubmission.dateSubmitted.seconds * 1000).toLocaleString()
        : typeof formSubmission.dateSubmitted === "string"
        ? formSubmission.dateSubmitted
        : "Unknown date";

    const submittedByInfo = formSubmission.submittedBy?.email || "Unknown";
    const residentName = formSubmission.residentFullName || "";

    doc.text(`Submitted by: ${submittedByInfo}`, leftMargin, yPosition);
    yPosition += 6;
    doc.text(`Date: ${submittedDate}`, leftMargin, yPosition);
    yPosition += 6;

    if (residentName) {
      doc.text(`Resident: ${residentName}`, leftMargin, yPosition);
      yPosition += 10;
    } else {
      yPosition += 4;
    }

    // ===== PROCESS FORM DATA =====
    const elementMetadata = formSubmission.responses?.elementMetadata || {};
    const elementOrder = formSubmission.responses?.elementOrder || [];
    const responses =
      formSubmission.responses?.responses || formSubmission.responses || {};

    // Prepare for table
    const tableData = [];
    const signatureFields = [];

    const fieldsToProcess =
      elementOrder.length > 0
        ? elementOrder.map((id) => ({ id, metadata: elementMetadata[id] }))
        : Object.entries(elementMetadata).map(([id, metadata]) => ({
            id,
            metadata,
          }));

    for (const { id, metadata } of fieldsToProcess) {
      if (!metadata) continue;
      const value = responses[id];
      const label = metadata.label || id;

      // Skip header images
      if (metadata.type === "headerImage") continue;

      // Signatures go in a separate array
      if (metadata.type === "signature") {
        signatureFields.push({ id, label, value });
        continue;
      }

      // Format display value
      let displayValue = "";
      if (value === undefined || value === null) {
        displayValue = "";
      } else if (metadata.type === "checkbox" || metadata.type === "yesNo") {
        displayValue = value === true ? "Yes" : "No";
      } else if (
        metadata.type === "date" &&
        typeof value === "object" &&
        "seconds" in value
      ) {
        displayValue = new Date(value.seconds * 1000).toLocaleDateString();
      } else if (
        metadata.valueType === "timestamp" &&
        typeof value === "object" &&
        "seconds" in value
      ) {
        displayValue = new Date(value.seconds * 1000).toLocaleDateString();
      } else if (Array.isArray(value)) {
        displayValue = value.join(", ");
      } else {
        displayValue = String(value || "");
      }

      tableData.push([label, displayValue]);
    }

    // ===== ADD FORM FIELDS TABLE =====
    if (tableData.length > 0) {
      autoTable(doc, {
        startY: yPosition,
        head: [["Field", "Value"]],
        body: tableData,
        theme: "grid",
        headStyles: {
          fillColor: [73, 80, 87],
          textColor: [255, 255, 255],
          fontStyle: "bold",
        },
        columnStyles: {
          0: { cellWidth: "auto", fontStyle: "bold" },
          1: { cellWidth: "auto" },
        },
        styles: {
          cellPadding: 5,
          fontSize: 10,
          overflow: "linebreak",
          lineWidth: 0.1,
        },
        margin: { left: leftMargin, right: leftMargin },
      });

      yPosition = doc.lastAutoTable.finalY + 15;
    }

    // ===== HANDLE SIGNATURES =====
    for (const { label, value } of signatureFields) {
      // New page check
      if (yPosition > doc.internal.pageSize.height - 60) {
        doc.addPage();
        yPosition = 20;
      }

      // Label
      doc.setFontSize(11);
      doc.setFont(undefined, "bold");
      doc.text(`${label}:`, leftMargin, yPosition);
      yPosition += 8;

      // Define the bounding box for the signature
      const signatureBoxWidth = pageWidth - leftMargin * 2;
      const signatureBoxHeight = 50; // Smaller box for the signature

      doc.setDrawColor(200, 200, 200);
      doc.setFillColor(255, 255, 255);
      doc.setLineWidth(0.5);

      // Draw the “box” (you can remove if you prefer no box)
      doc.roundedRect(
        leftMargin,
        yPosition,
        signatureBoxWidth,
        signatureBoxHeight,
        2,
        2,
        "S"
      );

      // If we have a signature
      if (value && value.startsWith("data:image/")) {
        try {
          const processedSignature = await invertWhiteToBlack(value);

          // Load image to measure original size
          const img = new Image();
          img.src = processedSignature;
          await img.decode();

          const originalWidth = img.width;
          const originalHeight = img.height;

          // We'll give ourselves a small margin inside the box
          const horizontalPadding = 5;
          const verticalPadding = 5;

          const maxSignatureWidth = signatureBoxWidth - horizontalPadding * 2;
          const maxSignatureHeight = signatureBoxHeight - verticalPadding * 2;

          // Start by constraining to max width
          let finalWidth = maxSignatureWidth;
          let finalHeight = (originalHeight / originalWidth) * finalWidth;

          // If too tall, constrain by height
          if (finalHeight > maxSignatureHeight) {
            finalHeight = maxSignatureHeight;
            finalWidth = (originalWidth / originalHeight) * finalHeight;
          }

          // Center the image inside the box
          const imageX = leftMargin + (signatureBoxWidth - finalWidth) / 2; // horizontally centered
          const imageY = yPosition + (signatureBoxHeight - finalHeight) / 2; // vertically centered

          doc.addImage(
            processedSignature,
            "PNG",
            imageX,
            imageY,
            finalWidth,
            finalHeight
          );
        } catch (error) {
          console.error("Error adding signature to PDF:", error);
          doc.setFontSize(10);
          doc.setFont(undefined, "italic");
          doc.setTextColor(0, 0, 0);
          doc.text(
            "[Error displaying signature]",
            leftMargin + 10,
            yPosition + 15
          );
        }
      } else {
        // No signature
        doc.setFontSize(10);
        doc.setFont(undefined, "italic");
        doc.setTextColor(150, 150, 150);
        doc.text("[No signature provided]", leftMargin + 10, yPosition + 15);
      }

      yPosition += signatureBoxHeight + 20;
    }

    // ===== THANK YOU MESSAGE =====
    const spaceRemaining = doc.internal.pageSize.height - yPosition;
    if (spaceRemaining < 40) {
      doc.addPage();
      yPosition = 40;
    } else {
      yPosition += 20;
    }

    doc.setFontSize(11);
    doc.setFont(undefined, "italic");
    doc.text("Thank you for your submission!", pageWidth / 2, yPosition, {
      align: "center",
    });

    // ===== SAVE PDF =====
    const filename = `${
      formSubmission.formName
        ? formSubmission.formName.toLowerCase().replace(/\s+/g, "-")
        : "form-submission"
    }.pdf`;
    doc.save(filename);
  };

  const generateReceiptPDF = (payment, resident, organizationData = {}) => {
    const doc = new jsPDF();

    // Check if we have organization header data
    if (
      organizationData?.houseSettings?.headerLogo ||
      organizationData?.houseSettings?.headerTitle ||
      organizationData?.houseSettings?.headerContact
    ) {
      let yPosition = 10;

      // Add logo if exists with full width
      if (organizationData.houseSettings.headerLogo) {
        try {
          // Calculate the width of the page (with margins)
          const pageWidth = doc.internal.pageSize.width - 28; // 14 units margin on each side
          const logoHeight = 30; // Adjusted height for better appearance

          doc.addImage(
            organizationData.houseSettings.headerLogo,
            "JPEG",
            14,
            yPosition,
            pageWidth,
            logoHeight
          );
          yPosition += logoHeight + 8; // Add spacing after logo
        } catch (error) {
          console.error("Error adding logo to PDF:", error);
        }
      }

      // Add organization name if exists
      if (organizationData.houseSettings.headerTitle) {
        doc.setFontSize(18); // Larger font for title
        doc.setFont(undefined, "bold");
        doc.text(organizationData.houseSettings.headerTitle, 14, yPosition);
        yPosition += 10;
      }

      // Add contact info if exists
      if (organizationData.houseSettings.headerContact) {
        doc.setFontSize(10);
        doc.setFont(undefined, "normal");
        const contactLines =
          organizationData.houseSettings.headerContact.split("\n");
        contactLines.forEach((line) => {
          doc.text(line, 14, yPosition);
          yPosition += 5;
        });
        yPosition += 5;
      }

      // Add divider line
      doc.setLineWidth(0.5);
      doc.line(14, yPosition, doc.internal.pageSize.width - 14, yPosition);
      yPosition += 15; // Add more space after divider

      // Receipt title
      doc.setFontSize(20);
      doc.setFont(undefined, "bold");
      doc.text("PAYMENT RECEIPT", doc.internal.pageSize.width / 2, yPosition, {
        align: "center",
      });
      yPosition += 15;

      // Receipt details
      doc.setFontSize(10);
      doc.setFont(undefined, "normal");
      doc.text(
        `Receipt #: ${payment.id || new Date().getTime()}`,
        14,
        yPosition
      );
      yPosition += 5;

      // Safely convert paymentDate to a Date object if needed
      const paymentDate =
        payment.paymentDate && typeof payment.paymentDate.toDate === "function"
          ? payment.paymentDate.toDate()
          : payment.paymentDate;

      doc.text(
        `Date: ${new Date(paymentDate).toLocaleDateString()}`,
        14,
        yPosition
      );
      yPosition += 10;

      if (payment.startDate && payment.endDate) {
        yPosition += 5;
        doc.text(
          `Service Period: ${new Date(
            payment.startDate
          ).toLocaleDateString()} - ${new Date(
            payment.endDate
          ).toLocaleDateString()}`,
          14,
          yPosition
        );
        yPosition += 10;
      }

      // Resident Information
      doc.setFontSize(12);
      doc.setFont(undefined, "bold");
      doc.text("Received From:", 14, yPosition);
      yPosition += 7;
      doc.setFont(undefined, "normal");
      doc.text(`${resident.firstName} ${resident.lastName}`, 14, yPosition);
      yPosition += 10;

      // Payment Information
      doc.setFontSize(12);
      doc.setFont(undefined, "bold");
      doc.text("Payment Information:", 14, yPosition);
      yPosition += 7;
      doc.setFont(undefined, "normal");

      const paymentInfoTable = [
        ["Payment Type:", payment.paymentType || "Cash"],
        ["Amount:", `$${parseFloat(payment.paymentAmount).toFixed(2)}`],
        [
          "Previous Balance:",
          `$${parseFloat(payment.previousBalance).toFixed(2)}`,
        ],
        ["New Balance:", `$${parseFloat(payment.newBalance).toFixed(2)}`],
      ];

      // Add invoice number if exists
      if (payment.invoiceNumber) {
        paymentInfoTable.push(["Invoice Number:", payment.invoiceNumber]);
      }

      // Add invoice week if exists
      if (payment.invoiceWeek) {
        paymentInfoTable.push(["Invoice Week:", payment.invoiceWeek]);
      }

      // Add funding source if exists
      if (payment.fundingSource) {
        paymentInfoTable.push(["Funding Source:", payment.fundingSource]);
      }

      // Generate the table
      autoTable(doc, {
        startY: yPosition,
        body: paymentInfoTable,
        theme: "plain",
        styles: {
          cellPadding: 3,
        },
        columnStyles: {
          0: { fontStyle: "bold", cellWidth: 40 },
          1: { cellWidth: "auto" },
        },
      });

      // Get the Y position after the table
      yPosition = doc.lastAutoTable.finalY + 20;

      // Add notes if any
      if (payment.paymentNote) {
        doc.setFontSize(11);
        doc.setFont(undefined, "bold");
        doc.text("Notes:", 14, yPosition);
        yPosition += 7;
        doc.setFont(undefined, "normal");
        const noteLines = doc.splitTextToSize(payment.paymentNote, 180);
        doc.text(noteLines, 14, yPosition);
      }

      // Add thank you message at the bottom
      yPosition = doc.internal.pageSize.height - 40;
      doc.setFontSize(11);
      doc.setFont(undefined, "italic");
      doc.text(
        "Thank you for your payment!",
        doc.internal.pageSize.width / 2,
        yPosition,
        { align: "center" }
      );
    } else {
      // Simple version without header
      doc.setFontSize(20);
      doc.setFont(undefined, "bold");
      doc.text("PAYMENT RECEIPT", doc.internal.pageSize.width / 2, 20, {
        align: "center",
      });

      let yPosition = 40;

      doc.setFontSize(10);
      doc.setFont(undefined, "normal");
      doc.text(
        `Receipt #: ${payment.id || new Date().getTime()}`,
        14,
        yPosition
      );
      yPosition += 5;

      // Safely convert paymentDate to a Date object if needed
      const paymentDate =
        payment.paymentDate && typeof payment.paymentDate.toDate === "function"
          ? payment.paymentDate.toDate()
          : payment.paymentDate;

      doc.text(
        `Date: ${new Date(paymentDate).toLocaleDateString()}`,
        14,
        yPosition
      );
      yPosition += 10;

      doc.setFontSize(12);
      doc.setFont(undefined, "bold");
      doc.text("Received From:", 14, yPosition);
      yPosition += 7;
      doc.setFont(undefined, "normal");
      doc.text(`${resident.firstName} ${resident.lastName}`, 14, yPosition);
      yPosition += 10;

      doc.setFontSize(12);
      doc.setFont(undefined, "bold");
      doc.text("Payment Information:", 14, yPosition);
      yPosition += 7;
      doc.setFont(undefined, "normal");

      const paymentInfoTable = [
        ["Payment Type:", payment.paymentType || "Cash"],
        ["Amount:", `$${parseFloat(payment.paymentAmount).toFixed(2)}`],
        [
          "Previous Balance:",
          `$${parseFloat(payment.previousBalance).toFixed(2)}`,
        ],
        ["New Balance:", `$${parseFloat(payment.newBalance).toFixed(2)}`],
      ];

      if (payment.invoiceNumber) {
        paymentInfoTable.push(["Invoice Number:", payment.invoiceNumber]);
      }

      if (payment.invoiceWeek) {
        paymentInfoTable.push(["Invoice Week:", payment.invoiceWeek]);
      }

      if (payment.fundingSource) {
        paymentInfoTable.push(["Funding Source:", payment.fundingSource]);
      }

      autoTable(doc, {
        startY: yPosition,
        body: paymentInfoTable,
        theme: "plain",
        styles: {
          cellPadding: 3,
        },
        columnStyles: {
          0: { fontStyle: "bold", cellWidth: 40 },
          1: { cellWidth: "auto" },
        },
      });

      yPosition = doc.lastAutoTable.finalY + 20;

      if (payment.paymentNote) {
        doc.setFontSize(11);
        doc.setFont(undefined, "bold");
        doc.text("Notes:", 14, yPosition);
        yPosition += 7;
        doc.setFont(undefined, "normal");
        const noteLines = doc.splitTextToSize(payment.paymentNote, 180);
        doc.text(noteLines, 14, yPosition);
      }

      yPosition = doc.internal.pageSize.height - 40;
      doc.setFontSize(11);
      doc.setFont(undefined, "italic");
      doc.text(
        "Thank you for your payment!",
        doc.internal.pageSize.width / 2,
        yPosition,
        { align: "center" }
      );
    }

    return doc;
  };

  const handleDownloadReceipt = (payment) => {
    if (!residentProfile) {
      console.error("Resident data not found");
      return;
    }
    // If payment.paymentDate is a Firestore Timestamp, it has a toDate method.
    // Otherwise, assume it's already a Date.
    const paymentDate =
      payment.paymentDate && typeof payment.paymentDate.toDate === "function"
        ? payment.paymentDate.toDate()
        : payment.paymentDate;

    const doc = generateReceiptPDF(payment, residentProfile, organizationData);
    const filename = `receipt_${residentProfile.lastName}_${new Date(
      paymentDate
    )
      .toISOString()
      .slice(0, 10)}.pdf`;
    doc.save(filename);
  };

  const getQuestionLabel = (key, submission) =>
    submission.elementMetadata?.[key]?.label || formatFormLabel(key);

  const toggleResidentInfo = () => {
    setIsResidentInfoOpen((prev) => !prev);
  };

  const toggleNotesModal = () => {
    const newState = !isNotesModalOpen;
    setIsNotesModalOpen(newState);

    // Only fetch notes when opening the modal and we don't have them yet
    if (newState && residentNotes.length === 0) {
      fetchNotes();
    }
  };

  const toggleAddNoteModal = () => {
    const newState = !isAddNoteModalOpen;
    setIsAddNoteModalOpen(newState);

    // Only fetch users when opening the Add Note modal
    if (newState) {
      fetchUsers();
    }
  };

  const togglePaymentHistoryModal = () => {
    const newState = !paymentHistoryModalOpen;
    setPaymentHistoryModalOpen(newState);

    // Only fetch payment history when opening the modal and we don't have them yet
    if (newState && paymentHistory.length === 0) {
      fetchPaymentHistory();
    }
  };

  const toggleUaResultsModal = async () => {
    const newState = !uaResultsModalOpen;
    setUaResultsModalOpen(newState);

    // Only fetch when opening the modal
    if (newState) {
      // Fetch UA results if we don't have them yet
      if (uaResults.length === 0) {
        await fetchUaResults();
      }

      // Fetch available substances if we don't have them yet
      if (Object.keys(availableSubstances).length === 0) {
        try {
          const houseSettingsRef = doc(
            db,
            `organizations/${organizationId}/houseSettings/initial`
          );
          const houseSettingsDoc = await getDoc(houseSettingsRef);

          if (houseSettingsDoc.exists()) {
            const data = houseSettingsDoc.data();
            if (data.substances && Object.keys(data.substances).length > 0) {
              setAvailableSubstances(data.substances);
            }
          }
        } catch (err) {
          console.error("Error fetching house settings:", err);
        }
      }
    }
  };

  const toggleRecoveryInfo = () => {
    const newState = !isRecoveryInfoOpen;
    setIsRecoveryInfoOpen(newState);

    // Only fetch phases when opening the section and we don't have them yet
    if (newState && phases.length === 0 && checkFeature("phases")) {
      fetchPhases();
    }
  };

  const toggleMedicationInfo = () => {
    setIsMedicationInfoOpen((prevState) => !prevState);
  };

  const toggleEmergencyContact = () => {
    setIsEmergencyContactOpen((prevState) => !prevState);
  };

  const toggleHousingInfo = () => {
    setIsHousingInfoOpen((prevState) => !prevState);
  };

  const toggleLegalInfo = () => {
    setIsLegalInfoOpen((prevState) => !prevState);
  };

  const handleDropdownClick = () => {
    const newDropdownState = !isDropdownOpen;
    setIsDropdownOpen(newDropdownState);

    // Only fetch dropdown items when opening the dropdown and we don't have them yet
    if (newDropdownState && dropdownItems.length === 0) {
      fetchDropdownItems();
    }
  };

  const formatFormLabel = (key) => {
    // Remove the 'element-checkbox-' or similar prefixes
    const cleanKey = key.replace(/^element-(checkbox|input|signature)-/, "");

    // Capitalize the first letter
    return cleanKey.charAt(0).toUpperCase() + cleanKey.slice(1);
  };

  const handleDocumentClick = (item) => {
    if (item.type === "fileUpload") {
      setEditingDoc(item);
      setEditedName(item.saveAs);
    } else if (item.type === "formSubmission") {
      setViewingFormSubmission(item);
    }
  };

  const handleDeleteDocument = async (docId) => {
    try {
      await deleteDoc(
        doc(
          db,
          `organizations/${organizationId}/residents/${residentId}/fileUploads`,
          docId
        )
      );
      setDocuments((prevDocs) => prevDocs.filter((d) => d.id !== docId));
    } catch (error) {
      console.error("Error deleting file:", error);
    }
  };

  const handleUpdateDocument = async () => {
    try {
      await updateDoc(
        doc(
          db,
          `organizations/${organizationId}/residents/${residentId}/fileUploads`,
          editingDoc.id
        ),
        {
          saveAs: editedName,
        }
      );
      setDocuments((prevDocs) =>
        prevDocs.map((d) =>
          d.id === editingDoc.id ? { ...d, saveAs: editedName } : d
        )
      );
      setEditingDoc(null);
    } catch (error) {
      console.error("Error updating document:", error);
    }
  };

  function DomainScore({ latestScore, firstScore, label }) {
    const difference = latestScore - firstScore;

    return (
      <div className="flex-1 bg-transparent p-4 rounded-lg text-center border border-red-500 m-2">
        <h3 className="text-lg font-mono mb-2">{label}</h3>
        <p>
          {latestScore ?? "-"}
          {difference !== 0 && (
            <sup style={{ color: difference > 0 ? "green" : "red" }}>
              {difference > 0 ? "+" : ""}
              {difference}
            </sup>
          )}
        </p>
      </div>
    );
  }

  if (!residentProfile) return <div>Loading...</div>;

  return (
    <div className="min-h-screen bg-[#18181B] bg-gradient-to-br from-zinc-900 via-zinc-900/95 to-zinc-950 p-6 relative overflow-hidden">
      {/* Ambient background effects */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(24,24,27,0.3),rgba(0,0,0,0))]" />
      <div className="absolute inset-0">
        <div className="absolute top-0 -left-4 w-96 h-96 bg-cyan-500/10 rounded-full mix-blend-screen filter blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full mix-blend-screen filter blur-3xl animate-pulse delay-700" />
      </div>

      <div className="max-w-7xl mx-auto space-y-8 relative">
        {/* Header */}
        <div className="flex items-center gap-6 mb-12">
          <div className="flex gap-3">
            <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse" />
            <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse delay-100 opacity-70" />
            <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse delay-200 opacity-40" />
          </div>
          <div className="flex flex-col">
            <h1 className="text-4xl font-bold text-white tracking-tight">
              {residentProfile?.firstName} {residentProfile?.lastName}
            </h1>
            <p className="text-slate-400">Resident Profile</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap justify-between items-center gap-4 mb-8">
          {/* Left Buttons */}
          <div className="flex flex-wrap gap-3 w-full sm:w-auto">
            {canViewNotes && (
              <button
                onClick={toggleNotesModal}
                className="px-4 py-2 bg-slate-800/50 hover:bg-slate-800/70 text-slate-300 hover:text-white rounded-lg transition-colors flex items-center gap-2 w-full sm:w-auto"
              >
                <Plus size={20} />
                Resident Notes
              </button>
            )}

            {/* File Input */}
            <input
              type="file"
              id="fileInput"
              hidden
              multiple
              onChange={handleFileChange}
            />

            {canViewPaymentHistory && (
              <button
                onClick={togglePaymentHistoryModal}
                className="px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 rounded-lg transition-colors flex items-center gap-2 w-full sm:w-auto"
              >
                <DollarSign size={20} />
                Payment History
              </button>
            )}
            {canViewUaHistory && (
              <button
                onClick={toggleUaResultsModal}
                className="px-4 py-2 bg-purple-500/10 hover:bg-purple-500/20 text-purple-400 hover:text-purple-300 rounded-lg transition-colors flex items-center gap-2 w-full sm:w-auto"
              >
                <TestTube size={20} />
                Resident UA's
              </button>
            )}

            {/* Documents Dropdown Container */}
            <div className="relative inline-block w-full sm:w-auto">
              <button
                onClick={handleDropdownClick}
                className="px-4 py-2.5 bg-slate-800/80 hover:bg-slate-700/80 text-blue-400 rounded-lg transition-colors flex items-center gap-2 w-full sm:w-[220px] justify-between"
              >
                <div className="flex items-center gap-2">
                  <FileText size={20} />
                  <span>Resident Forms</span>
                </div>
                {isDropdownOpen ? (
                  <ChevronDown className="ml-1" size={16} />
                ) : (
                  <ChevronRight className="ml-1" size={16} />
                )}
              </button>

              {isDropdownOpen && (
                <div className="absolute z-50 mt-2 w-full bg-slate-800/95 rounded-lg border border-slate-700 shadow-xl">
                  <button
                    onClick={() => {
                      setIsFormCompletionModalOpen(true);
                      setIsDropdownOpen(false);
                    }}
                    className="w-full px-4 py-3 flex items-center gap-2 text-blue-400 hover:bg-slate-700/50 transition-colors"
                  >
                    <ClipboardList size={18} />
                    <span className="text-sm">Complete Forms</span>
                  </button>

                  {/* Separator */}
                  <div className="border-t border-slate-700 my-2"></div>
                  {dropdownItems.length > 0 ? (
                    <div className="divide-y divide-slate-700">
                      {dropdownItems.length > 0 ? (
                        <div className="divide-y divide-slate-700">
                          {dropdownItems.map((item) => {
                            const isDocumentOrFileUpload =
                              item.type === "document" ||
                              item.type === "fileUpload";

                            return (
                              <div
                                key={item.id}
                                className="flex items-center justify-between px-4 py-3 hover:bg-slate-700/50 transition-colors cursor-pointer"
                                onClick={() => handleDocumentClick(item)}
                              >
                                {/* Display `saveAs` for documents or fileUploads; otherwise use formName */}
                                <p className="text-slate-300 text-sm text-left truncate mr-4">
                                  {isDocumentOrFileUpload
                                    ? item.saveAs
                                    : item.formName}
                                </p>

                                {isDocumentOrFileUpload ? (
                                  <a
                                    href={item.fileURL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-400 hover:text-blue-300 text-sm font-medium"
                                  >
                                    View
                                  </a>
                                ) : (
                                  // For form submissions or anything else, show a non-link label
                                  <span className="text-blue-400 hover:text-blue-300 text-sm font-medium">
                                    View
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="px-4 py-3">
                          <p className="text-slate-400 text-sm">
                            No items available.
                          </p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="px-4 py-3">
                      <p className="text-slate-400 text-sm">
                        No items available.
                      </p>
                    </div>
                  )}

                  {/* Add Document Button */}
                  {canAddForms && (
                    <div className="border-t border-slate-700">
                      <button
                        onClick={() => {
                          setIsModalOpen(true);
                          setIsDropdownOpen(false);
                        }}
                        className="w-full px-4 py-3 flex items-center gap-2 text-blue-400 hover:bg-slate-700/50 transition-colors"
                      >
                        <Plus size={18} />
                        <span className="text-sm">Add Document</span>
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Edit Document Modal */}
              {editingDoc && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
                  <div className="bg-slate-900/90 border border-slate-700 rounded-xl shadow-lg p-6 w-full max-w-md">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-500/10 rounded-lg">
                          <Edit2 className="text-blue-400" size={20} />
                        </div>
                        <h2 className="text-xl font-medium text-white">
                          Edit Document
                        </h2>
                      </div>
                      <button
                        onClick={() => setEditingDoc(null)}
                        className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
                      >
                        <X
                          className="text-slate-400 hover:text-slate-300"
                          size={20}
                        />
                      </button>
                    </div>

                    <div className="space-y-6">
                      <div className="relative">
                        <input
                          type="text"
                          value={editedName}
                          onChange={(e) => setEditedName(e.target.value)}
                          className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors peer"
                        />
                        <label className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-blue-400 transition-colors">
                          Document Name
                        </label>
                      </div>

                      <div className="flex justify-between gap-4">
                        {checkPermission("deleteResidentDoc") && (
                          <button
                            onClick={() => {
                              handleDeleteDocument(editingDoc.id);
                              setEditingDoc(null); // Close modal after delete
                            }}
                            className="px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 rounded-lg transition-colors flex items-center gap-2 flex-1"
                          >
                            <Trash2 size={20} />
                            Delete
                          </button>
                        )}
                        {checkPermission("updateResidentDoc") && (
                          <button
                            onClick={handleUpdateDocument}
                            className="px-4 py-2 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 hover:text-blue-300 rounded-lg transition-colors flex items-center gap-2 flex-1"
                          >
                            <Edit2 size={20} />
                            Update
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Button */}
          <div className="w-full sm:w-auto flex gap-3">
            {(checkPermission("viewResidentDashboard") ||
              isLeaderViewingOwnProfile) &&
              organizationData?.houseSettings?.paidResDashboardCount > 0 && (
                <button
                  onClick={navigateToResidentDashboard}
                  className="px-6 py-3 bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white font-medium rounded-lg shadow-lg hover:shadow-indigo-500/25 transition-all duration-300 flex items-center gap-2 w-full sm:w-auto"
                >
                  <Home size={20} />
                  Resident's Dashboard
                </button>
              )}

            {canUpdateResidentInfo && (
              <button
                onClick={handleUpdate}
                className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-medium rounded-lg shadow-lg hover:shadow-blue-500/25 transition-all duration-300 flex items-center gap-2 w-full sm:w-auto"
              >
                <Save size={20} />
                Update Profile
              </button>
            )}
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Recovery Capital Scores or Alternative */}
          <div
            className={
              recoveryCapitalEnabled
                ? "lg:col-span-2 space-y-6"
                : "lg:col-span-2 space-y-6"
            }
          >
            {recoveryCapitalEnabled ? (
              <>
                {/* Domain Scores Cards */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <DomainScoreCard
                    label="General"
                    score={latestQuestionnaire?.generalCapitalScore}
                    firstScore={allQuestionnaires[0]?.generalCapitalScore}
                    colorClass="from-emerald-500/10 to-emerald-500/5"
                    borderClass="border-emerald-500/20"
                    textClass="text-emerald-400"
                    allQuestionnaires={allQuestionnaires}
                  />
                  <DomainScoreCard
                    label="Social"
                    score={latestQuestionnaire?.socialCapitalScore}
                    firstScore={allQuestionnaires[0]?.socialCapitalScore}
                    colorClass="from-blue-500/10 to-blue-500/5"
                    borderClass="border-blue-500/20"
                    textClass="text-blue-400"
                    allQuestionnaires={allQuestionnaires}
                  />
                  <DomainScoreCard
                    label="Physical"
                    score={latestQuestionnaire?.physicalCapitalScore}
                    firstScore={allQuestionnaires[0]?.physicalCapitalScore}
                    colorClass="from-yellow-500/10 to-yellow-500/5"
                    borderClass="border-yellow-500/20"
                    textClass="text-yellow-400"
                    allQuestionnaires={allQuestionnaires}
                  />
                  <DomainScoreCard
                    label="Individual"
                    score={latestQuestionnaire?.individualCapitalScore}
                    firstScore={allQuestionnaires[0]?.individualCapitalScore}
                    colorClass="from-purple-500/10 to-purple-500/5"
                    borderClass="border-purple-500/20"
                    textClass="text-purple-400"
                    allQuestionnaires={allQuestionnaires}
                  />
                  <DomainScoreCard
                    label="Spiritual"
                    score={latestQuestionnaire?.spiritualCapitalScore}
                    firstScore={allQuestionnaires[0]?.spiritualCapitalScore}
                    colorClass="from-pink-500/10 to-pink-500/5"
                    borderClass="border-pink-500/20"
                    textClass="text-pink-400"
                    allQuestionnaires={allQuestionnaires}
                  />
                  <DomainScoreCard
                    label="Total"
                    score={latestQuestionnaire?.recoveryCapitalTotal}
                    firstScore={allQuestionnaires[0]?.recoveryCapitalTotal}
                    colorClass="from-red-500/10 to-red-500/5"
                    borderClass="border-red-500/20"
                    textClass="text-red-400"
                    allQuestionnaires={allQuestionnaires}
                  />
                </div>

                {/* Chart Section */}
                <div className="bg-slate-900/50 backdrop-blur-xl rounded-xl border border-slate-700 p-6">
                  <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
                    {/* Dropdown for Domain Selection */}
                    <select
                      value={selectedDomain}
                      onChange={(e) => {
                        setSelectedDomain(e.target.value);
                        setChartType(
                          e.target.value === "domainScores"
                            ? "domainScores"
                            : "questions"
                        );
                      }}
                      className="bg-slate-800/50 text-slate-300 border border-slate-700 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 w-full sm:w-auto"
                    >
                      <option value="domainScores">Domain Scores</option>
                      <option value="General">General</option>
                      <option value="Social">Social</option>
                      <option value="Physical">Physical</option>
                      <option value="Individual">Individual</option>
                      <option value="Spiritual">Spiritual</option>
                    </select>

                    {/* Export Button */}
                    {recoveryCapitalEnabled && canExportResidentData && (
                      <button
                        onClick={exportToCSV}
                        className="px-4 py-2 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 hover:text-blue-300 rounded-lg transition-colors flex items-center gap-2 w-full sm:w-auto"
                      >
                        <Download size={20} />
                        Export CSV
                      </button>
                    )}
                  </div>

                  {/* Chart Container */}
                  <div className="h-[250px] sm:h-[400px] w-full">
                    <ResponsiveContainer
                      width="100%"
                      height="100%"
                      className="!mx-0 sm:!mx-auto" // Remove excessive margins on mobile
                    >
                      {allQuestionnaires.length === 1 ? (
                        <BarChart
                          data={allQuestionnaires}
                          margin={{
                            top: 10,
                            right: window.innerWidth < 640 ? 10 : 30, // Reduce right margin on mobile
                            left: window.innerWidth < 640 ? 0 : 20, // Remove unnecessary left margin on mobile
                            bottom: 5,
                          }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip content={<CustomTooltip />} />
                          {/* Bars for domainScores */}
                          {chartType === "domainScores" ? (
                            <>
                              <Bar
                                dataKey="generalCapitalScore"
                                fill="#8884d8"
                                name="General"
                              >
                                <LabelList
                                  dataKey="generalCapitalScore"
                                  position="top"
                                />
                              </Bar>
                              <Bar
                                dataKey="socialCapitalScore"
                                fill="#82ca9d"
                                name="Social"
                              >
                                <LabelList
                                  dataKey="socialCapitalScore"
                                  position="top"
                                />
                              </Bar>
                              <Bar
                                dataKey="physicalCapitalScore"
                                fill="#ffc658"
                                name="Physical"
                              >
                                <LabelList
                                  dataKey="physicalCapitalScore"
                                  position="top"
                                />
                              </Bar>
                              <Bar
                                dataKey="individualCapitalScore"
                                fill="#ff7300"
                                name="Individual"
                              >
                                <LabelList
                                  dataKey="individualCapitalScore"
                                  position="top"
                                />
                              </Bar>
                              <Bar
                                dataKey="spiritualCapitalScore"
                                fill="#413ea0"
                                name="Spiritual"
                              >
                                <LabelList
                                  dataKey="spiritualCapitalScore"
                                  position="top"
                                />
                              </Bar>
                              <Bar
                                dataKey="recoveryCapitalTotal"
                                fill="#ff0000"
                                name="Total"
                              >
                                <LabelList
                                  dataKey="recoveryCapitalTotal"
                                  position="top"
                                />
                              </Bar>
                            </>
                          ) : (
                            // Render Bars for selected domain questions
                            domainQuestions[selectedDomain].map(
                              (questionId, index) => (
                                <Bar
                                  key={questionId}
                                  dataKey={questionId}
                                  fill={getStrokeColor(index)}
                                  name={questionId}
                                >
                                  <LabelList
                                    dataKey={questionId}
                                    position="top"
                                  />
                                </Bar>
                              )
                            )
                          )}
                        </BarChart>
                      ) : (
                        <LineChart
                          data={allQuestionnaires}
                          margin={{
                            top: 10,
                            right: window.innerWidth < 640 ? 10 : 30, // Reduce for mobile
                            left: window.innerWidth < 640 ? 0 : 20, // Adjust margins on smaller screens
                            bottom: 5,
                          }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip content={<CustomTooltip />} />
                          {/* Render Lines for domainScores */}
                          {chartType === "domainScores" ? (
                            <>
                              {[
                                "generalCapitalScore",
                                "socialCapitalScore",
                                "physicalCapitalScore",
                                "individualCapitalScore",
                                "spiritualCapitalScore",
                                "recoveryCapitalTotal",
                              ].map((dataKey, index) => (
                                <Line
                                  key={dataKey}
                                  type="monotone"
                                  dataKey={dataKey}
                                  stroke={getStrokeColor(index)}
                                  strokeWidth={3}
                                  name={dataKey}
                                />
                              ))}
                            </>
                          ) : (
                            // Render Lines for selected domain questions
                            domainQuestions[selectedDomain].map(
                              (questionId, index) => (
                                <Line
                                  key={questionId}
                                  type="monotone"
                                  dataKey={questionId}
                                  stroke={getStrokeColor(index)}
                                  strokeWidth={3}
                                  name={questionId}
                                />
                              )
                            )
                          )}
                        </LineChart>
                      )}
                    </ResponsiveContainer>
                  </div>
                  {/* Questionnaire Buttons */}
                  {recoveryCapitalEnabled && (
                    <div className="flex flex-wrap justify-center pt-2 gap-3 mb-8 relative z-10">
                      {[...allQuestionnaires]
                        .sort((a, b) => {
                          const order = [
                            "Intake",
                            "3 Months",
                            "6 Months",
                            "9 Months",
                            "1 Year",
                          ];
                          return (
                            order.indexOf(a.questionnaire) -
                            order.indexOf(b.questionnaire)
                          );
                        })
                        .map((questionnaire, index) => (
                          <button
                            key={index}
                            onClick={() =>
                              handleQuestionnaireClick(questionnaire.id)
                            }
                            className="px-4 py-2 bg-slate-800/50 hover:bg-slate-800/70 text-slate-300 hover:text-white rounded-lg transition-colors border border-slate-700 hover:border-slate-600"
                          >
                            {questionnaire.questionnaire || "Unknown"}
                          </button>
                        ))}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                {/* Alternative content when recovery capital is disabled */}
                <div className="bg-slate-900/50 backdrop-blur-xl rounded-xl border border-slate-700 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-blue-500/10 rounded-lg">
                      <User className="text-blue-400" />
                    </div>
                    <h2 className="text-xl text-white font-medium">
                      Resident Summary
                    </h2>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Basic info summary */}
                    <div className="space-y-3">
                      <div className="flex justify-between items-center p-3 bg-slate-800/50 rounded-lg">
                        <span className="text-slate-300">House:</span>
                        <span className="text-white font-medium">
                          {editableProfile?.house || "Not assigned"}
                        </span>
                      </div>

                      <div className="flex justify-between items-center p-3 bg-slate-800/50 rounded-lg">
                        <span className="text-slate-300">Intake Date:</span>
                        <span className="text-white font-medium">
                          {editableProfile?.intakeDate
                            ? new Date(
                                editableProfile.intakeDate
                              ).toLocaleDateString()
                            : "Not set"}
                        </span>
                      </div>

                      <div className="flex justify-between items-center p-3 bg-slate-800/50 rounded-lg">
                        <span className="text-slate-300">Sobriety Date:</span>
                        <span className="text-white font-medium">
                          {editableProfile?.sobrietyDate
                            ? new Date(
                                editableProfile.sobrietyDate
                              ).toLocaleDateString()
                            : "Not set"}
                        </span>
                      </div>
                    </div>

                    {/* Recent activity or status */}
                    <div className="space-y-3">
                      <div className="flex justify-between items-center p-3 bg-slate-800/50 rounded-lg">
                        <span className="text-slate-300">Rent Status:</span>
                        <span className="text-emerald-400 font-medium">
                          Current
                        </span>
                      </div>

                      <div className="flex justify-between items-center p-3 bg-slate-800/50 rounded-lg">
                        <span className="text-slate-300">
                          Meeting Requirements:
                        </span>
                        <span className="text-white font-medium">
                          {meetingRequirements || 0} per week
                        </span>
                      </div>

                      <div className="flex justify-between items-center p-3 bg-slate-800/50 rounded-lg">
                        <span className="text-slate-300">Recent UA:</span>
                        <span className="text-white font-medium">
                          {uaResults && uaResults.length > 0
                            ? new Date(uaResults[0].date).toLocaleDateString()
                            : "None recorded"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Recent activity timeline */}
                <div className="bg-slate-900/50 backdrop-blur-xl rounded-xl border border-slate-700 p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-purple-500/10 rounded-lg">
                      <Calendar className="text-purple-400" />
                    </div>
                    <h2 className="text-xl text-white font-medium">
                      Recent Activity
                    </h2>
                  </div>

                  <div className="space-y-4">
                    {/* Show recent forms/documents/payments/UAs */}
                    {[...dropdownItems, ...paymentHistory, ...uaResults]
                      .sort((a, b) => {
                        const dateA =
                          a.date ||
                          a.dateSubmitted ||
                          a.paymentDate ||
                          new Date();
                        const dateB =
                          b.date ||
                          b.dateSubmitted ||
                          b.paymentDate ||
                          new Date();
                        return new Date(dateB) - new Date(dateA);
                      })
                      .slice(0, 5)
                      .map((item, index) => {
                        // Determine the item type and prepare click handler
                        let clickHandler;
                        let itemType = "";

                        if ("formName" in item) {
                          // This is a form submission
                          clickHandler = () => setViewingFormSubmission(item);
                          itemType = item.formName;
                        } else if ("paymentAmount" in item) {
                          // This is a payment
                          clickHandler = () => setPaymentHistoryModalOpen(true);
                          itemType = "Payment";
                        } else if ("results" in item) {
                          // This is a UA test
                          clickHandler = () => {
                            setUaResultsModalOpen(true);
                            // You could potentially add logic here to scroll to or highlight this specific UA
                          };
                          itemType = "UA Test";
                        } else if ("saveAs" in item) {
                          // This is a document
                          clickHandler = () => {
                            setEditingDoc(item);
                            setEditedName(item.saveAs);
                          };
                          itemType = item.saveAs;
                        }

                        return (
                          <div
                            key={index}
                            className="p-3 bg-slate-800/50 rounded-lg border border-slate-700 hover:bg-slate-700/50 hover:border-slate-600 transition-colors cursor-pointer"
                            onClick={clickHandler}
                          >
                            <div className="flex justify-between">
                              <span className="text-slate-300">{itemType}</span>
                              <span className="text-slate-400 text-sm">
                                {new Date(
                                  item.date ||
                                    item.dateSubmitted?.seconds * 1000 ||
                                    item.paymentDate
                                ).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              </>
            )}
          </div>
          <div className="w-full space-y-4">
            {/* Resident Information */}
            <div className="bg-slate-900/50 backdrop-blur-xl rounded-xl border border-slate-700 group transition-all duration-300">
              <button onClick={toggleResidentInfo} className="w-full">
                <div
                  className={`${
                    isResidentInfoOpen ? "rounded-t-xl" : "rounded-xl"
                  } p-4 flex justify-between items-center`}
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-500/10 rounded-lg">
                      <User className="text-blue-400" />
                    </div>
                    <h2 className="text-xl text-white font-medium">
                      Resident Information
                    </h2>
                  </div>
                  <div className="bg-slate-800 rounded-lg p-2 group-hover:bg-slate-700 transition-colors">
                    {isResidentInfoOpen ? (
                      <ChevronDown className="text-slate-400" size={24} />
                    ) : (
                      <ChevronRight className="text-slate-400" size={24} />
                    )}
                  </div>
                </div>
              </button>
              {isResidentInfoOpen && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  className="p-6 border-t border-slate-700/50 space-y-6"
                >
                  <div className="relative">
                    <input
                      className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-blue-500 transition-colors peer"
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={editableProfile?.firstName ?? ""}
                      onChange={(e) =>
                        handleChange("firstName", e.target.value)
                      }
                    />
                    <label
                      className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-blue-400 transition-colors"
                      htmlFor="firstName"
                    >
                      First Name
                    </label>
                  </div>

                  <div className="relative">
                    <input
                      className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-blue-500 transition-colors peer"
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={editableProfile?.lastName ?? ""}
                      onChange={(e) => handleChange("lastName", e.target.value)}
                    />
                    <label
                      className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-blue-400 transition-colors"
                      htmlFor="lastName"
                    >
                      Last Name
                    </label>
                  </div>

                  <div className="relative">
                    <input
                      className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-blue-500 transition-colors peer"
                      type="date"
                      id="dob"
                      name="dob"
                      value={editableProfile?.dob ?? ""}
                      onChange={(e) => handleChange("dob", e.target.value)}
                    />
                    <label
                      className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-blue-400 transition-colors"
                      htmlFor="dob"
                    >
                      Date of Birth
                    </label>
                  </div>
                  <div className="relative">
                    <select
                      className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-blue-500 transition-colors peer"
                      id="gender"
                      name="gender"
                      value={editableProfile?.gender || ""}
                      onChange={(e) => handleChange("gender", e.target.value)}
                    >
                      <option value="">Select gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Trans">Trans</option>
                      <option value="Other">Other</option>
                    </select>
                    <label
                      className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-blue-400 transition-colors"
                      htmlFor="gender"
                    >
                      Gender
                    </label>
                  </div>
                  <div className="relative">
                    <input
                      className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-blue-500 transition-colors peer"
                      type="email"
                      id="email"
                      name="email"
                      value={editableProfile?.email ?? ""}
                      onChange={(e) => handleChange("email", e.target.value)}
                    />
                    <label
                      className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-blue-400 transition-colors"
                      htmlFor="email"
                    >
                      Email Address
                    </label>
                  </div>

                  {/* Work Status field */}
                  <div className="relative">
                    <select
                      className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-blue-500 transition-colors peer"
                      id="workStatus"
                      name="workStatus"
                      value={editableProfile?.workStatus || ""}
                      onChange={(e) => handleWorkStatusChange(e.target.value)}
                    >
                      <option value="">Select work status</option>
                      <option value="Full-Time">Full-Time</option>
                      <option value="Part-Time">Part-Time</option>
                      <option value="Retired">Retired</option>
                      <option value="Unemployed">Unemployed</option>
                    </select>
                    <label
                      className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-blue-400 transition-colors"
                      htmlFor="workStatus"
                    >
                      Work Status
                    </label>
                  </div>

                  {/* Display Employment History if any exists */}
                  {editableProfile?.employmentHistory &&
                    editableProfile.employmentHistory.length > 0 && (
                      <div className="mt-4">
                        <h4 className="text-slate-300 text-sm mb-2">
                          Employment History
                        </h4>
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {editableProfile.employmentHistory.map(
                            (record, index) => (
                              <div
                                key={index}
                                className="bg-slate-800/50 rounded-lg p-3 text-sm border border-slate-700"
                              >
                                <div className="flex justify-between mb-1">
                                  <span className="text-blue-400 font-medium">
                                    {record.status}
                                  </span>
                                  <span className="text-slate-400">
                                    {new Date(record.date).toLocaleDateString()}
                                  </span>
                                </div>
                                {record.employer && (
                                  <div className="text-slate-300">
                                    Employer: {record.employer}
                                  </div>
                                )}
                                {record.notes && (
                                  <div className="text-slate-400 text-xs mt-1">
                                    {record.notes}
                                  </div>
                                )}
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )}
                  <div className="relative">
                    <select
                      className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-blue-500 transition-colors peer"
                      id="veteranStatus"
                      name="veteranStatus"
                      value={editableProfile?.veteranStatus || ""}
                      onChange={(e) =>
                        handleChange("veteranStatus", e.target.value)
                      }
                    >
                      <option value="">Select veteran status</option>
                      <option value="Veteran">Veteran</option>
                      <option value="No military background">
                        No military background
                      </option>
                    </select>
                    <label
                      className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-blue-400 transition-colors"
                      htmlFor="veteranStatus"
                    >
                      Veteran Status
                    </label>
                  </div>

                  <div className="relative">
                    <input
                      className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-blue-500 transition-colors peer"
                      type="text"
                      id="phoneNumber"
                      name="phoneNumber"
                      value={
                        formatPhoneNumber(editableProfile.phoneNumber) || ""
                      } // Use the formatting function here
                      onChange={(e) => {
                        const rawValue = e.target.value.replace(/\D/g, "");
                        handleChange("phoneNumber", rawValue); // Save raw value to editableProfile
                      }}
                    />
                    <label
                      className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-blue-400 transition-colors"
                      htmlFor="phoneNumber"
                    >
                      Phone Number
                    </label>
                  </div>
                </div>
              )}
            </div>

            {/* Housing Information */}
            <div className="bg-slate-900/50 backdrop-blur-xl rounded-xl border border-slate-700 group transition-all duration-300">
              <button onClick={toggleHousingInfo} className="w-full">
                <div
                  className={`${
                    isHousingInfoOpen ? "rounded-t-xl" : "rounded-xl"
                  } p-4 flex justify-between items-center`}
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-500/10 rounded-lg">
                      <Home className="text-emerald-400" />
                    </div>
                    <h2 className="text-xl text-white font-medium">Housing</h2>
                  </div>
                  <div className="bg-slate-800 rounded-lg p-2 group-hover:bg-slate-700 transition-colors">
                    {isHousingInfoOpen ? (
                      <ChevronDown className="text-slate-400" size={24} />
                    ) : (
                      <ChevronRight className="text-slate-400" size={24} />
                    )}
                  </div>
                </div>
              </button>
              {isHousingInfoOpen && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  className="p-6 border-t border-slate-700/50 space-y-6"
                >
                  <div className="relative">
                    <select
                      className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-emerald-500 transition-colors appearance-none peer"
                      name="house"
                      id="house"
                      value={editableProfile?.house ?? ""}
                      onChange={(e) => handleHouseChange(e.target.value)}
                    >
                      <option value="" disabled>
                        Select a house
                      </option>
                      {Object.keys(housesData).map((houseId) => (
                        <option key={houseId} value={houseId}>
                          {houseId}
                        </option>
                      ))}
                    </select>
                    <label
                      className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-emerald-400 transition-colors"
                      htmlFor="house"
                    >
                      House
                    </label>
                  </div>

                  {currentHouseMoveInDate && (
                    <div className="relative">
                      <input
                        className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-emerald-500 transition-colors peer"
                        type="date"
                        id="currentHouseMoveIn"
                        name="currentHouseMoveIn"
                        value={currentHouseMoveInDate}
                        onChange={(e) => {
                          const houseHistory = [
                            ...editableProfile.houseHistory,
                          ];
                          houseHistory[houseHistory.length - 1].moveInDate =
                            e.target.value;
                          handleChange("houseHistory", houseHistory);
                        }}
                      />
                      <label
                        className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-emerald-400 transition-colors"
                        htmlFor="currentHouseMoveIn"
                      >
                        Current House Move-in Date
                      </label>
                    </div>
                  )}

                  {editableProfile?.house &&
                    housesData[editableProfile.house]?.moveInDateRequired &&
                    editableProfile.intakeDate && // Ensure intakeDate exists
                    Object.keys(editableProfile).some(
                      (key) =>
                        key.endsWith("MoveInDate") &&
                        key !== `${editableProfile.house}MoveInDate`
                    ) && ( // Ensure at least one other MoveInDate exists
                      <div className="relative">
                        <input
                          className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-emerald-500 transition-colors peer"
                          type="date"
                          id="moveInDate"
                          name="moveInDate"
                          value={
                            editableProfile[
                              `${editableProfile.house}MoveInDate`
                            ] ?? ""
                          }
                          onChange={(e) =>
                            handleChange(
                              `${editableProfile.house}MoveInDate`,
                              e.target.value
                            )
                          }
                        />
                        <label
                          className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-emerald-400 transition-colors"
                          htmlFor="moveInDate"
                        >
                          {housesData[editableProfile.house].name} Move-in Date
                        </label>
                      </div>
                    )}

                  <div className="relative">
                    <input
                      className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-emerald-500 transition-colors peer"
                      type="number"
                      id="rentAmount"
                      name="rentAmount"
                      value={editableProfile?.rentAmount ?? ""}
                      onChange={(e) =>
                        handleChange("rentAmount", e.target.value)
                      }
                    />
                    <label
                      className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-emerald-400 transition-colors"
                      htmlFor="rentAmount"
                    >
                      Rent Amount
                    </label>
                  </div>

                  <div className="relative">
                    <select
                      className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-emerald-500 transition-colors appearance-none peer"
                      name="rentSchedule"
                      id="schedule"
                      value={editableProfile?.rentSchedule ?? ""}
                      onChange={(e) =>
                        handleChange("rentSchedule", e.target.value)
                      }
                    >
                      <option value="">Select a schedule</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                    <label
                      className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-emerald-400 transition-colors"
                      htmlFor="schedule"
                    >
                      Rent Schedule
                    </label>
                  </div>

                  <div className="relative">
                    <input
                      className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-emerald-500 transition-colors peer"
                      type="date"
                      id="date"
                      name="intakeDate"
                      value={editableProfile?.intakeDate ?? ""}
                      onChange={(e) =>
                        handleChange("intakeDate", e.target.value)
                      }
                    />
                    <label
                      className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-emerald-400 transition-colors"
                      htmlFor="date"
                    >
                      Intake Date
                    </label>
                  </div>
                </div>
              )}

              {showMoveInDateModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex justify-center items-center">
                  <div className="bg-slate-900/90 border border-slate-700 rounded-xl shadow-lg p-6 w-full max-w-md">
                    <h3 className="text-xl font-semibold text-white mb-4">
                      Set Move-in Date
                    </h3>
                    <div className="space-y-4">
                      <div className="relative">
                        <input
                          type="date"
                          value={newHouseMoveInDate}
                          onChange={(e) =>
                            setNewHouseMoveInDate(e.target.value)
                          }
                          className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-emerald-500 transition-colors peer"
                        />
                        <label className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-emerald-400 transition-colors">
                          Move-in Date for {pendingHouseChange}
                        </label>
                      </div>

                      <div className="flex justify-end gap-3 mt-6">
                        <button
                          onClick={cancelHouseChange}
                          className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={confirmHouseChange}
                          disabled={!newHouseMoveInDate}
                          className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Confirm
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Emergency Contact */}
            <div className="bg-slate-900/50 backdrop-blur-xl rounded-xl border border-slate-700 group transition-all duration-300">
              <button onClick={toggleEmergencyContact} className="w-full">
                <div
                  className={`${
                    isEmergencyContactOpen ? "rounded-t-xl" : "rounded-xl"
                  } p-4 flex justify-between items-center`}
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-yellow-500/10 rounded-lg">
                      <Phone className="text-yellow-400" />
                    </div>
                    <h2 className="text-xl text-white font-medium">
                      Emergency Contact
                    </h2>
                  </div>
                  <div className="bg-slate-800 rounded-lg p-2 group-hover:bg-slate-700 transition-colors">
                    {isEmergencyContactOpen ? (
                      <ChevronDown className="text-slate-400" size={24} />
                    ) : (
                      <ChevronRight className="text-slate-400" size={24} />
                    )}
                  </div>
                </div>
              </button>
              {isEmergencyContactOpen && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  className="p-6 border-t border-slate-700/50 space-y-6"
                >
                  {/* First Name */}
                  <div className="relative">
                    <input
                      className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-blue-500 transition-colors peer"
                      type="text"
                      id="emergencyfname"
                      name="ecFirstName"
                      value={editableProfile?.ecFirstName ?? ""}
                      onChange={(e) =>
                        handleChange("ecFirstName", e.target.value)
                      }
                    />
                    <label
                      className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-blue-400 transition-colors"
                      htmlFor="emergencyfname"
                    >
                      First Name
                    </label>
                  </div>

                  {/* Last Name */}
                  <div className="relative">
                    <input
                      className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-blue-500 transition-colors peer"
                      type="text"
                      id="emergencylname"
                      name="ecLastName"
                      value={editableProfile?.ecLastName ?? ""}
                      onChange={(e) =>
                        handleChange("ecLastName", e.target.value)
                      }
                    />
                    <label
                      className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-blue-400 transition-colors"
                      htmlFor="emergencylname"
                    >
                      Last Name
                    </label>
                  </div>

                  {/* Relationship */}
                  <div className="relative">
                    <input
                      className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-blue-500 transition-colors peer"
                      type="text"
                      id="emergencyrelation"
                      name="ecRelationship"
                      value={editableProfile?.ecRelationship ?? ""}
                      onChange={(e) =>
                        handleChange("ecRelationship", e.target.value)
                      }
                    />
                    <label
                      className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-blue-400 transition-colors"
                      htmlFor="emergencyrelation"
                    >
                      Relationship
                    </label>
                  </div>

                  {/* Phone Number */}
                  <div className="relative">
                    <input
                      className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-blue-500 transition-colors peer"
                      type="tel"
                      id="ecPhoneNumber"
                      name="ecPhoneNumber"
                      value={editableProfile.ecPhoneNumber ?? ""}
                      onChange={(e) => {
                        const rawValue = e.target.value.replace(/\D/g, "");
                        const formattedValue = formatPhoneNumber(rawValue);
                        setFormattedPhoneNumber(formattedValue);
                        handleChange("ecPhoneNumber", rawValue); // Save raw value to editableProfile
                      }}
                    />
                    <label
                      className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-blue-400 transition-colors"
                      htmlFor="ecPhoneNumber"
                    >
                      Emergency Phone Number
                    </label>
                  </div>
                </div>
              )}
            </div>

            {/* Medication */}
            <div className="relative z-40 bg-slate-900/50 backdrop-blur-xl rounded-xl border border-slate-700 group transition-all duration-300">
              <button onClick={toggleMedicationInfo} className="w-full">
                <div
                  className={`${
                    isMedicationInfoOpen ? "rounded-t-xl" : "rounded-xl"
                  } p-4 flex justify-between items-center`}
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-500/10 rounded-lg">
                      <Pill className="text-purple-400" />
                    </div>
                    <h2 className="text-xl text-white font-medium">
                      Medication
                    </h2>
                  </div>
                  <div className="bg-slate-800 rounded-lg p-2 group-hover:bg-slate-700 transition-colors">
                    {isMedicationInfoOpen ? (
                      <ChevronDown className="text-slate-400" size={24} />
                    ) : (
                      <ChevronRight className="text-slate-400" size={24} />
                    )}
                  </div>
                </div>
              </button>
              {isMedicationInfoOpen && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  className="p-6 border-t border-slate-700/50 space-y-6"
                >
                  {/* MAT Selection */}
                  <div className="relative">
                    <select
                      className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-purple-500 transition-colors appearance-none peer"
                      name="mat"
                      id="mat"
                      value={editableProfile?.isMAT ? "Yes" : "No"}
                      onChange={(e) =>
                        handleMATStatusChange(e.target.value === "Yes")
                      }
                    >
                      <option value="">Select an option</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                    <label
                      className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-purple-400 transition-colors"
                      htmlFor="mat"
                    >
                      MAT?
                    </label>
                  </div>

                  {editableProfile?.matHistory &&
                    editableProfile.matHistory.length > 0 && (
                      <div className="mt-4">
                        <h4 className="text-slate-300 text-sm mb-2">
                          MAT History
                        </h4>
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {editableProfile.matHistory.map((record, index) => (
                            <div
                              key={index}
                              className="bg-slate-800/50 rounded-lg p-3 text-sm border border-slate-700"
                            >
                              <div className="flex justify-between mb-1">
                                <span className="text-purple-400 font-medium">
                                  {record.status}
                                </span>
                                <span className="text-slate-400">
                                  {new Date(record.date).toLocaleDateString()}
                                </span>
                              </div>
                              {record.notes && (
                                <div className="text-slate-400 text-xs mt-1">
                                  {record.notes}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                  {/* Medications */}
                  {editableProfile?.medications.map((medication, index) => (
                    <div key={index} className="grid grid-cols-2 gap-4">
                      <div className="relative flex-1">
                        <input
                          className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white
               border border-slate-600 focus:border-purple-500 transition-colors
               placeholder-slate-400"
                          type="text"
                          name="medicationName"
                          value={medication.name}
                          onChange={(e) => handleMedicationNameInput(e, index)}
                          onBlur={() =>
                            setTimeout(() => {
                              setMedicationSuggestions([]);
                              setActiveMedicationIndex(null);
                            }, 200)
                          } /* allow clicks */
                          placeholder="Medication Name"
                        />

                        {/* suggestions dropdown */}
                        {medicationSuggestions.length > 0 &&
                          activeMedicationIndex === index && (
                            <div
                              className="absolute top-full left-0 w-full mt-1 bg-slate-800
                    border border-slate-700 rounded-lg shadow-lg z-50"
                            >
                              {medicationSuggestions.map((s, i) => (
                                <div
                                  key={i}
                                  onClick={() =>
                                    handleSelectSuggestion(s, index)
                                  }
                                  className="px-4 py-2 hover:bg-slate-700 cursor-pointer text-white"
                                >
                                  <div className="font-semibold">
                                    {s.brandName} ({s.genericName})
                                  </div>
                                  <div className="text-sm text-slate-400">
                                    {s.type}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                      </div>

                      <div className="relative">
                        <input
                          className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-purple-500 transition-colors peer"
                          type="text"
                          id={`medicationdose-${index}`}
                          name="medicationDose"
                          value={medication.dose ?? ""}
                          onChange={(e) =>
                            handleMedicationChange(
                              index,
                              "dose",
                              e.target.value
                            )
                          }
                        />
                        <label
                          className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-purple-400 transition-colors"
                          htmlFor={`medicationdose-${index}`}
                        >
                          Dose
                        </label>
                      </div>
                    </div>
                  ))}

                  {/* Add Medication Button */}
                  <div className="flex justify-center gap-4">
                    <button
                      onClick={addMedication}
                      className="px-4 py-2 bg-purple-500/10 hover:bg-purple-500/20 text-purple-400 hover:text-purple-300 rounded-lg transition-colors flex items-center gap-2"
                    >
                      <Plus size={20} />
                      Add Medication
                    </button>

                    <button
                      onClick={() => navigate(`/medlog/${residentId}`)}
                      className="px-4 py-2 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 hover:text-cyan-300 rounded-lg transition-colors flex items-center gap-2"
                    >
                      <Pill size={20} />
                      Medication Log
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Recovery Info */}
            <div className="bg-slate-900/50 backdrop-blur-xl rounded-xl border border-slate-700 group transition-all duration-300">
              <button onClick={toggleRecoveryInfo} className="w-full">
                <div
                  className={`${
                    isRecoveryInfoOpen ? "rounded-t-xl" : "rounded-xl"
                  } p-4 flex justify-between items-center`}
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-pink-500/10 rounded-lg">
                      <Heart className="text-pink-400" />
                    </div>
                    <h2 className="text-xl text-white font-medium">
                      Recovery Info
                    </h2>
                  </div>
                  <div className="bg-slate-800 rounded-lg p-2 group-hover:bg-slate-700 transition-colors">
                    {isRecoveryInfoOpen ? (
                      <ChevronDown className="text-slate-400" size={24} />
                    ) : (
                      <ChevronRight className="text-slate-400" size={24} />
                    )}
                  </div>
                </div>
              </button>
              {isRecoveryInfoOpen && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  className="p-6 border-t border-slate-700/50 space-y-6"
                >
                  {/* Referral Location */}
                  <div className="relative">
                    <select
                      className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-pink-500 transition-colors appearance-none peer"
                      name="referralLocation"
                      id="refLocation"
                      value={editableProfile?.referralLocation ?? ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "add_new_option") {
                          // Open the modal instead of using prompt
                          setIsModalOpen(true);
                          return;
                        }

                        handleChange("referralLocation", value);
                        if (value !== "Other") {
                          handleChange("customReferralLocation", "");
                        }
                      }}
                    >
                      <option value="">Select a referral location</option>
                      {referralOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                      <option
                        value="add_new_option"
                        className="text-slate-400 italic"
                        style={{ fontStyle: "italic", color: "#94a3b8" }}
                      >
                        + Add new referral source
                      </option>
                    </select>
                    <label
                      className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-pink-400 transition-colors"
                      htmlFor="refLocation"
                    >
                      Referral Location
                    </label>
                  </div>

                  {/* Custom Referral Location */}
                  {editableProfile?.referralLocation === "Other" && (
                    <div className="relative">
                      <input
                        className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-pink-500 transition-colors peer"
                        type="text"
                        id="customReferralLocation"
                        name="customReferralLocation"
                        value={editableProfile?.customReferralLocation ?? ""}
                        onChange={(e) =>
                          handleChange("customReferralLocation", e.target.value)
                        }
                        onBlur={(e) => {
                          if (e.target.value.trim()) {
                            handleAddReferralOption(e.target.value.trim());
                          }
                        }}
                      />
                      <label
                        className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-pink-400 transition-colors"
                        htmlFor="customReferralLocation"
                      >
                        Custom Referral Location
                      </label>
                    </div>
                  )}

                  {checkFeature("phases") && (
                    <div className="relative">
                      <select
                        className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-pink-500 transition-colors peer"
                        id="phase"
                        name="phase"
                        value={editableProfile?.phase || ""}
                        onChange={(e) => handleChange("phase", e.target.value)}
                      >
                        <option value="">Select phase</option>
                        {phases && phases.length > 0 ? (
                          phases.map((phase) => (
                            <option key={phase.id} value={phase.name}>
                              {phase.name}
                            </option>
                          ))
                        ) : (
                          <option value="" disabled>
                            No phases available
                          </option>
                        )}
                      </select>
                      <label
                        className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-pink-400 transition-colors"
                        htmlFor="phase"
                      >
                        Recovery Phase
                      </label>
                    </div>
                  )}

                  {/* Sobriety Date */}
                  <div className="relative">
                    <input
                      className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-pink-500 transition-colors peer"
                      type="date"
                      id="sobrietyDate"
                      name="sobrietyDate"
                      value={editableProfile?.sobrietyDate ?? ""}
                      onChange={(e) =>
                        handleChange("sobrietyDate", e.target.value)
                      }
                    />
                    <label
                      className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-pink-400 transition-colors"
                      htmlFor="sobrietyDate"
                    >
                      Sobriety Date
                    </label>
                  </div>
                  {/* Meeting Requirements */}
                  <div className="relative">
                    <input
                      type="number"
                      id="meetingRequirements"
                      className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-pink-500 transition-colors peer"
                      value={meetingRequirements}
                      onChange={(e) =>
                        setMeetingRequirements(Number(e.target.value))
                      }
                      onBlur={async () => {
                        // Save to Firestore when user finishes editing
                        try {
                          const residentDocRef = doc(
                            db,
                            `organizations/${organizationId}/residents`,
                            residentId
                          );
                          await updateDoc(residentDocRef, {
                            meetingRequirements,
                          });
                        } catch (error) {
                          console.error(
                            "Error updating meeting requirements:",
                            error
                          );
                        }
                      }}
                    />
                    <label
                      className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-pink-400 transition-colors"
                      htmlFor="meetingRequirements"
                    >
                      Meeting Requirements
                    </label>
                  </div>

                  {/* Drug of Choice */}
                  <div className="relative">
                    <Select
                      className="rounded-lg"
                      isMulti
                      options={[
                        { value: "Heroin", label: "Heroin" },
                        { value: "Meth", label: "Meth" },
                        { value: "THC", label: "THC" },
                        { value: "Alcohol", label: "Alcohol" },
                        { value: "Crack", label: "Crack" },
                        { value: "Amphetamines", label: "Amphetamines" },
                        { value: "Cocaine", label: "Cocaine" },
                        { value: "Cough Syrup", label: "Cough Syrup" },
                      ]}
                      value={
                        editableProfile?.drugOfChoice &&
                        Array.isArray(editableProfile?.drugOfChoice)
                          ? editableProfile?.drugOfChoice.map((choice) => ({
                              value: choice,
                              label: choice,
                            }))
                          : []
                      }
                      onChange={(selectedOptions) => {
                        const newChoices = selectedOptions.map(
                          (option) => option.value
                        );
                        handleChange("drugOfChoice", newChoices);
                      }}
                      styles={{
                        control: (base) => ({
                          ...base,
                          backgroundColor: "rgba(30, 41, 59, 0.5)",
                          borderColor: "rgb(71, 85, 105)",
                          "&:hover": {
                            borderColor: "rgb(219, 39, 119)",
                          },
                        }),
                        menu: (base) => ({
                          ...base,
                          zIndex: 1050, // Ensure it appears above other elements
                          position: "absolute", // Prevent overlapping due to container styles
                          backgroundColor: "rgb(30, 41, 59)",
                          border: "1px solid rgb(71, 85, 105)",
                        }),
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 1050, // Additional z-index boost if needed
                        }),
                        option: (base, state) => ({
                          ...base,
                          backgroundColor: state.isSelected
                            ? "rgb(219, 39, 119)"
                            : "transparent",
                          "&:hover": {
                            backgroundColor: "rgba(219, 39, 119, 0.1)",
                          },
                        }),
                        multiValue: (base) => ({
                          ...base,
                          backgroundColor: "rgba(219, 39, 119, 0.1)",
                        }),
                        multiValueLabel: (base) => ({
                          ...base,
                          color: "rgb(219, 39, 119)",
                        }),
                        multiValueRemove: (base) => ({
                          ...base,
                          color: "rgb(219, 39, 119)",
                          "&:hover": {
                            backgroundColor: "rgba(219, 39, 119, 0.2)",
                            color: "rgb(219, 39, 119)",
                          },
                        }),
                      }}
                      menuPlacement="auto"
                      menuPortalTarget={document.body} // Ensure menu renders outside of parent overflow
                    />
                    <label className="absolute left-4 -top-6 text-xs text-slate-400 transition-colors">
                      Drug of Choice
                    </label>
                  </div>
                </div>
              )}

              {isModalOpen && (
                <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-[200]">
                  <div className="bg-slate-900 p-6 rounded-lg shadow-lg max-w-md w-full border border-slate-800">
                    <h2 className="text-white text-lg font-semibold mb-4">
                      Add New Referral Source
                    </h2>
                    <input
                      type="text"
                      value={newReferral}
                      onChange={(e) => setNewReferral(e.target.value)}
                      className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      placeholder="Enter new referral source"
                    />
                    <div className="flex justify-end gap-4 mt-4">
                      <button
                        onClick={() => {
                          setIsModalOpen(false);
                          setNewReferral(""); // Clear input
                        }}
                        className="px-4 py-2 bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700 border border-slate-700 transition-all"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => {
                          handleAddReferralOption(newReferral);
                          setIsModalOpen(false);
                          setNewReferral("");
                        }}
                        className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-all"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Legal */}
            <div className="bg-slate-900/50 backdrop-blur-xl rounded-xl border border-slate-700 group transition-all duration-300">
              <button onClick={toggleLegalInfo} className="w-full">
                <div
                  className={`${
                    isLegalInfoOpen ? "rounded-t-xl" : "rounded-xl"
                  } p-4 flex justify-between items-center`}
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-indigo-500/10 rounded-lg">
                      <Scale className="text-indigo-400" />
                    </div>
                    <h2 className="text-xl text-white font-medium">Legal</h2>
                  </div>
                  <div className="bg-slate-800 rounded-lg p-2 group-hover:bg-slate-700 transition-colors">
                    {isLegalInfoOpen ? (
                      <ChevronDown className="text-slate-400" size={24} />
                    ) : (
                      <ChevronRight className="text-slate-400" size={24} />
                    )}
                  </div>
                </div>
              </button>
              {isLegalInfoOpen && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  className="p-6 border-t border-slate-700/50 space-y-6"
                >
                  {/* PO First Name */}
                  <div className="relative">
                    <input
                      className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-indigo-500 transition-colors peer"
                      type="text"
                      id="poFirstName"
                      name="poFirstName"
                      value={editableProfile?.poFirstName ?? ""}
                      onChange={(e) =>
                        handlePOFieldChange("poFirstName", e.target.value)
                      }
                    />
                    <label
                      className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-indigo-400 transition-colors"
                      htmlFor="poFirstName"
                    >
                      PO First Name
                    </label>
                  </div>

                  {/* PO Last Name */}
                  <div className="relative">
                    <input
                      className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-indigo-500 transition-colors peer"
                      type="text"
                      id="poLastName"
                      name="poLastName"
                      value={editableProfile?.poLastName ?? ""}
                      onChange={(e) =>
                        handlePOFieldChange("poLastName", e.target.value)
                      }
                    />
                    <label
                      className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-indigo-400 transition-colors"
                      htmlFor="poLastName"
                    >
                      PO Last Name
                    </label>
                  </div>

                  {editableProfile?.probationHistory &&
                    editableProfile.probationHistory.length > 0 && (
                      <div className="mt-4">
                        <h4 className="text-slate-300 text-sm mb-2">
                          Probation/Parole History
                        </h4>
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {editableProfile.probationHistory.map(
                            (record, index) => (
                              <div
                                key={index}
                                className="bg-slate-800/50 rounded-lg p-3 text-sm border border-slate-700"
                              >
                                <div className="flex justify-between mb-1">
                                  <span className="text-indigo-400 font-medium">
                                    {record.status}
                                  </span>
                                  <span className="text-slate-400">
                                    {new Date(record.date).toLocaleDateString()}
                                  </span>
                                </div>
                                {record.notes && (
                                  <div className="text-slate-400 text-xs mt-1">
                                    {record.notes}
                                  </div>
                                )}
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )}

                  {/* County */}
                  <div className="relative">
                    <input
                      className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-indigo-500 transition-colors peer"
                      type="text"
                      id="county"
                      name="county"
                      value={editableProfile?.county ?? ""}
                      onChange={(e) => handleChange("county", e.target.value)}
                    />
                    <label
                      className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-indigo-400 transition-colors"
                      htmlFor="county"
                    >
                      County
                    </label>
                  </div>

                  {/* Pending Charges */}
                  <div className="relative">
                    <input
                      className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-indigo-500 transition-colors peer"
                      type="text"
                      id="pendingCharges"
                      name="pendingCharges"
                      value={editableProfile?.pendingCharges ?? ""}
                      onChange={(e) =>
                        handleChange("pendingCharges", e.target.value)
                      }
                    />
                    <label
                      className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-indigo-400 transition-colors"
                      htmlFor="pendingCharges"
                    >
                      Pending Charges
                    </label>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* UA Results Modal */}
      {uaResultsModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex justify-center items-center">
          <div className="bg-slate-900/90 border border-slate-700 rounded-xl shadow-lg w-full max-w-2xl">
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/10 rounded-lg">
                  <TestTube className="text-purple-400" size={20} />
                </div>
                <h3 className="text-xl font-semibold text-white">
                  UA Results History
                </h3>
              </div>
              <button
                onClick={() => setUaResultsModalOpen(false)}
                className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
              >
                <X className="text-slate-400 hover:text-slate-300" size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 max-h-[70vh] overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-800">
              <div className="space-y-4">
                {/* Header Row */}
                <div className="grid grid-cols-2 gap-4 font-medium pb-2 sticky top-0 bg-slate-900/95 backdrop-blur-sm z-10 p-2 rounded-lg">
                  <p className="text-center text-slate-300">Date</p>
                  <p className="text-center text-slate-300">Result</p>
                </div>

                {/* UA Result Rows */}
                {uaResults.map((result) => (
                  <details key={result.id} className="group">
                    <summary className="grid grid-cols-2 gap-4 items-center cursor-pointer p-3 bg-slate-800/50 rounded-lg border border-slate-700 hover:border-slate-600 transition-colors">
                      <p className="text-center text-slate-300">
                        {new Date(result.date).toLocaleDateString()}
                      </p>
                      <p
                        className={`text-center font-medium ${
                          Object.values(result.results).some(
                            (status) => status === "fail"
                          )
                            ? "text-red-400"
                            : "text-emerald-400"
                        }`}
                      >
                        {Object.entries(result.results)
                          .filter(
                            ([key, status]) =>
                              key !== "summary" && status === "fail"
                          )
                          .map(([substance]) => substance).length > 0
                          ? `Failed: ${Object.entries(result.results)
                              .filter(
                                ([key, status]) =>
                                  key !== "summary" && status === "fail"
                              )
                              .map(([substance]) => substance)
                              .join(", ")}`
                          : "Passed All"}
                      </p>
                    </summary>
                    <div className="mt-2 p-4 bg-slate-800/30 rounded-lg border border-slate-700">
                      <div className="grid grid-cols-2 gap-4">
                        {Object.entries(result.results)
                          .filter(
                            ([key]) =>
                              key !== "summary" &&
                              // Only show substances that are enabled in the organization settings
                              (Object.keys(availableSubstances).length === 0 ||
                                availableSubstances[key] === true)
                          )
                          .map(([substance, status]) => (
                            <div
                              key={substance}
                              className="flex justify-between items-center"
                            >
                              <span className="text-slate-300 capitalize">
                                {substance}:
                              </span>
                              <span
                                className={`font-medium ${
                                  status === "pass"
                                    ? "text-emerald-400"
                                    : "text-red-400"
                                }`}
                              >
                                {status}
                              </span>
                            </div>
                          ))}
                      </div>
                    </div>
                  </details>
                ))}

                {uaResults.length === 0 && (
                  <p className="text-center text-slate-400 py-4">
                    No UA results found for this resident.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Questionnaire Modal */}
      {isQuestionnaireModalOpen && selectedQuestionnaireData && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex justify-center items-center p-4">
          <div className="bg-slate-900/95 border border-slate-700 rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="relative px-8 py-6 border-b border-slate-700/50 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-1">
                    {selectedQuestionnaireData.questionnaire} Assessment
                  </h2>
                  <p className="text-slate-400 text-sm">
                    Completed on{" "}
                    {selectedQuestionnaireData.timestamp
                      ? new Date(
                          selectedQuestionnaireData.timestamp.seconds * 1000
                        ).toLocaleDateString("en-US", {
                          weekday: "long",
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "Date not available"}
                  </p>
                </div>
                <button
                  onClick={() => setQuestionnaireModalOpen(false)}
                  className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
                >
                  <X
                    className="text-slate-400 hover:text-slate-300"
                    size={24}
                  />
                </button>
              </div>

              {/* Total Score Banner */}
              {selectedQuestionnaireData.recoveryCapitalTotal && (
                <div className="mt-4 p-4 bg-slate-800/50 rounded-xl border border-slate-600/30">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-300 font-medium">
                      Total Recovery Capital Score
                    </span>
                    <span className="text-3xl font-bold text-white">
                      {selectedQuestionnaireData.recoveryCapitalTotal}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Content */}
            <div className="p-8 max-h-[60vh] overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-800">
              {/* Staff Notes Section - Moved to top */}
              {selectedQuestionnaireData.staffNotes && (
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    Staff Notes
                    <div className="h-px flex-1 bg-gradient-to-r from-slate-700 to-transparent"></div>
                  </h3>
                  <div className="bg-slate-800/30 rounded-xl p-6 border border-slate-700/50">
                    <p className="text-slate-300 leading-relaxed">
                      {selectedQuestionnaireData.staffNotes}
                    </p>
                  </div>
                </div>
              )}

              {/* Domain Scores Section */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  Domain Scores
                  <div className="h-px flex-1 bg-gradient-to-r from-slate-700 to-transparent"></div>
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  {[
                    {
                      key: "generalCapitalScore",
                      label: "General",
                      color: "emerald",
                    },
                    {
                      key: "socialCapitalScore",
                      label: "Social",
                      color: "blue",
                    },
                    {
                      key: "physicalCapitalScore",
                      label: "Physical",
                      color: "yellow",
                    },
                    {
                      key: "individualCapitalScore",
                      label: "Individual",
                      color: "purple",
                    },
                    {
                      key: "spiritualCapitalScore",
                      label: "Spiritual",
                      color: "pink",
                    },
                  ].map(({ key, label, color }) => {
                    const colorClasses = {
                      emerald: "border-emerald-500/30 bg-emerald-500/5",
                      blue: "border-blue-500/30 bg-blue-500/5",
                      yellow: "border-yellow-500/30 bg-yellow-500/5",
                      purple: "border-purple-500/30 bg-purple-500/5",
                      pink: "border-pink-500/30 bg-pink-500/5",
                    };

                    if (!selectedQuestionnaireData[key]) return null;

                    return (
                      <div
                        key={key}
                        className={`p-4 rounded-xl border ${colorClasses[color]}`}
                      >
                        <div className="text-center">
                          <div className="text-sm text-slate-400 mb-1">
                            {label}
                          </div>
                          <div className="text-2xl font-bold text-white">
                            {selectedQuestionnaireData[key]}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Questions by Domain Section */}
              <div className="space-y-8">
                {Object.entries(domainQuestions).map(
                  ([domainName, questionIds]) => {
                    // Filter questions that exist in the questionnaire data
                    const domainQuestionsData = questionIds
                      .map((questionId) => {
                        const value = selectedQuestionnaireData[questionId];
                        if (
                          value !== undefined &&
                          value !== null &&
                          value !== ""
                        ) {
                          return { questionId, value };
                        }
                        return null;
                      })
                      .filter(Boolean);

                    // Only render domain if it has questions
                    if (domainQuestionsData.length === 0) return null;

                    return (
                      <div key={domainName} className="mb-8">
                        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                          {domainName} Questions
                          <div className="h-px flex-1 bg-gradient-to-r from-slate-700 to-transparent"></div>
                        </h3>
                        <div className="space-y-3">
                          {domainQuestionsData.map(({ questionId, value }) => {
                            // Check if it's a Yes/No question
                            const isYesNo =
                              typeof value === "string" &&
                              (value === "Yes" || value === "No");

                            if (isYesNo) {
                              return (
                                <div
                                  key={questionId}
                                  className="p-4 bg-slate-800/20 rounded-lg border border-slate-700/30"
                                >
                                  <div className="flex items-center justify-between">
                                    <span className="text-slate-300 text-sm font-medium">
                                      {questionId.charAt(0).toUpperCase() +
                                        questionId
                                          .slice(1)
                                          .split(/(?=[A-Z])/)
                                          .join(" ")}
                                    </span>
                                    <span
                                      className={`px-3 py-1 rounded-full text-sm font-medium ${
                                        value === "Yes"
                                          ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
                                          : "bg-slate-500/20 text-slate-400 border border-slate-500/30"
                                      }`}
                                    >
                                      {value}
                                    </span>
                                  </div>
                                </div>
                              );
                            } else {
                              // Numeric questions with averages
                              return (
                                <div
                                  key={questionId}
                                  className="p-4 bg-slate-800/20 rounded-lg border border-slate-700/30"
                                >
                                  <div className="flex items-center justify-between gap-4">
                                    <label className="flex-1 text-slate-300 text-sm font-medium">
                                      {questionId.charAt(0).toUpperCase() +
                                        questionId
                                          .slice(1)
                                          .split(/(?=[A-Z])/)
                                          .join(" ")}
                                    </label>

                                    <div className="flex items-center gap-4">
                                      <input
                                        className="w-16 bg-slate-700/50 text-white font-semibold text-center rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-slate-700 transition-colors"
                                        type="text"
                                        value={value}
                                        onChange={(e) =>
                                          handleInputChange(
                                            questionId,
                                            e.target.value
                                          )
                                        }
                                      />

                                      {averageScores &&
                                        !isNaN(averageScores[questionId]) && (
                                          <div className="flex items-center gap-2 min-w-[8rem]">
                                            <div
                                              className={`w-3 h-3 rounded-full ${
                                                negativeQuestions.includes(
                                                  questionId
                                                )
                                                  ? value >
                                                    averageScores[questionId]
                                                    ? "bg-red-400"
                                                    : "bg-emerald-400"
                                                  : value <
                                                    averageScores[questionId]
                                                  ? "bg-red-400"
                                                  : "bg-emerald-400"
                                              }`}
                                            ></div>
                                            <span className="text-sm text-slate-400 font-medium">
                                              Avg:{" "}
                                              {averageScores[
                                                questionId
                                              ].toFixed(1)}
                                            </span>
                                          </div>
                                        )}
                                    </div>
                                  </div>
                                </div>
                              );
                            }
                          })}
                        </div>
                      </div>
                    );
                  }
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Notes Modal */}
      {isNotesModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex justify-center items-center">
          <div className="bg-slate-900/90 border border-slate-700 rounded-xl shadow-lg w-full max-w-2xl">
            <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-white">
                Resident Notes
              </h3>
              <button
                onClick={() => setIsNotesModalOpen(false)}
                className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
              >
                <X className="text-slate-400 hover:text-slate-300" size={20} />
              </button>
            </div>

            <div className="px-6 py-4">
              {canAddNote && (
                <button
                  onClick={toggleAddNoteModal}
                  className="mb-4 px-4 py-2 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 hover:text-blue-300 rounded-lg transition-colors flex items-center gap-2"
                >
                  <Plus size={20} />
                  Add Note
                </button>
              )}

              <div className="space-y-4 max-h-[60vh] overflow-y-auto">
                {residentNotes.map((note) => (
                  <div
                    key={note.id}
                    className="bg-slate-800/50 rounded-lg p-4 border border-slate-700"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <span className="text-slate-300 text-sm">
                          {note.date.toLocaleDateString()}
                        </span>
                        <h4 className="text-white font-medium">
                          {note.subject}
                        </h4>
                      </div>
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          note.severity === "High"
                            ? "bg-red-500/10 text-red-400"
                            : note.severity === "Medium"
                            ? "bg-yellow-500/10 text-yellow-400"
                            : "bg-green-500/10 text-green-400"
                        }`}
                      >
                        {note.severity}
                      </span>
                    </div>
                    <p className="text-slate-300">{note.note}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Note Modal */}
      {isAddNoteModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex justify-center items-center">
          <div className="bg-slate-900/90 border border-slate-700 rounded-xl shadow-lg w-full max-w-lg p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-white">Add Note</h3>
              <button
                onClick={() => setIsAddNoteModalOpen(false)}
                className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
              >
                <X className="text-slate-400 hover:text-slate-300" size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  Resident
                </label>
                <select
                  value={newNote.residentName}
                  onChange={(e) =>
                    setNewNote((prev) => ({
                      ...prev,
                      residentName: e.target.value,
                    }))
                  }
                  className="w-full bg-slate-800/50 rounded-lg px-4 py-2 text-white border border-slate-600"
                >
                  <option value={residentProfile?.fullName}>
                    {residentProfile?.fullName}
                  </option>
                  {residents
                    .filter(
                      (r) =>
                        r.currentResident &&
                        r.fullName !== residentProfile?.fullName
                    )
                    .map((resident) => (
                      <option key={resident.id} value={resident.fullName}>
                        {resident.fullName}
                      </option>
                    ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  Severity
                </label>
                <select
                  value={newNote.severity}
                  onChange={(e) =>
                    setNewNote((prev) => ({
                      ...prev,
                      severity: e.target.value,
                    }))
                  }
                  className="w-full bg-slate-800/50 rounded-lg px-4 py-2 text-white border border-slate-600"
                >
                  <option
                    value=""
                    disabled
                    className="text-slate-500"
                    style={{ color: "#64748b", fontStyle: "italic" }}
                  >
                    Select severity
                  </option>
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  Subject
                </label>
                <input
                  type="text"
                  value={newNote.subject}
                  onChange={(e) =>
                    setNewNote((prev) => ({ ...prev, subject: e.target.value }))
                  }
                  className="w-full bg-slate-800/50 rounded-lg px-4 py-2 text-white border border-slate-600"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  Note
                </label>
                <textarea
                  value={newNote.note}
                  onChange={(e) =>
                    setNewNote((prev) => ({ ...prev, note: e.target.value }))
                  }
                  className="w-full bg-slate-800/50 rounded-lg px-4 py-2 text-white border border-slate-600 min-h-[100px]"
                />
              </div>

              {/* New Notify Others Section */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  Notify Others
                </label>
                <Select
                  options={groupedUserOptions}
                  isMulti
                  value={newNote.notifyUsers || []}
                  onChange={(selectedOptions) =>
                    setNewNote((prev) => ({
                      ...prev,
                      notifyUsers: selectedOptions || [],
                    }))
                  }
                  placeholder="Select users or roles to notify..."
                  styles={{
                    control: (base) => ({
                      ...base,
                      backgroundColor: "rgba(30, 41, 59, 0.5)",
                      borderColor: "rgb(71, 85, 105)",
                      "&:hover": {
                        borderColor: "rgb(59, 130, 246)",
                      },
                    }),
                    menu: (base) => ({
                      ...base,
                      backgroundColor: "rgb(30, 41, 59)",
                      border: "1px solid rgb(71, 85, 105)",
                    }),
                    option: (base, state) => ({
                      ...base,
                      backgroundColor: state.isSelected
                        ? "rgba(59, 130, 246, 0.2)"
                        : state.isFocused
                        ? "rgba(59, 130, 246, 0.1)"
                        : "transparent",
                      color: state.isSelected ? "rgb(59, 130, 246)" : "white",
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: "rgba(59, 130, 246, 0.1)",
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: "rgb(59, 130, 246)",
                    }),
                    multiValueRemove: (base) => ({
                      ...base,
                      color: "rgb(59, 130, 246)",
                      "&:hover": {
                        backgroundColor: "rgba(59, 130, 246, 0.2)",
                        color: "rgb(59, 130, 246)",
                      },
                    }),
                  }}
                />
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setIsAddNoteModalOpen(false)}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitNote}
                  disabled={
                    !newNote.severity || !newNote.subject || !newNote.note
                  }
                  className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Save Note
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment History Modal */}
      {paymentHistoryModalOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900/90 border border-slate-700 rounded-xl shadow-lg w-full max-w-2xl max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="sticky top-0 px-6 py-4 border-b border-slate-700 flex items-center justify-between bg-slate-900/95 backdrop-blur-sm z-[102]">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-500/10 rounded-lg">
                  <DollarSign className="text-emerald-400" size={20} />
                </div>
                <h3 className="text-xl font-semibold text-white">
                  Payment History
                </h3>
              </div>
              <button
                onClick={() => setPaymentHistoryModalOpen(false)}
                className="relative z-[103] p-2 hover:bg-slate-800 rounded-lg transition-colors touch-auto"
              >
                <X className="text-slate-400 hover:text-slate-300" size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-hidden flex flex-col">
              {/* Sticky Column Headers - Hidden on mobile, visible on larger screens */}
              <div className="sticky top-0 px-6 pt-6 pb-2 bg-slate-900/95 backdrop-blur-sm z-[99] hidden md:block">
                <div className="grid grid-cols-5 gap-4 font-medium">
                  <p className="text-center text-slate-300">Date</p>
                  <p className="text-center text-slate-300">Type</p>
                  <p className="text-center text-slate-300">Amount</p>
                  <p className="text-center text-slate-300">New Balance</p>
                  <p className="text-center text-slate-300">Receipt</p>
                </div>
              </div>

              {/* Scrollable content area */}
              <div className="px-6 pb-6 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-800 overscroll-contain">
                <div className="space-y-4 pt-4">
                  {" "}
                  {/* Added more padding-top here */}
                  {/* Payment Rows */}
                  {paymentHistory
                    .sort((a, b) => {
                      // Handle different date formats safely
                      let dateA, dateB;

                      if (
                        a.paymentDate &&
                        typeof a.paymentDate.toDate === "function"
                      ) {
                        dateA = a.paymentDate.toDate();
                      } else if (a.paymentDate instanceof Date) {
                        dateA = a.paymentDate;
                      } else {
                        dateA = new Date(a.paymentDate || 0);
                      }

                      if (
                        b.paymentDate &&
                        typeof b.paymentDate.toDate === "function"
                      ) {
                        dateB = b.paymentDate.toDate();
                      } else if (b.paymentDate instanceof Date) {
                        dateB = b.paymentDate;
                      } else {
                        dateB = new Date(b.paymentDate || 0);
                      }

                      return dateB - dateA; // Sort descending (newest first)
                    })
                    .map((payment) => (
                      <details
                        key={payment.id}
                        className="group relative z-[100]"
                      >
                        <summary className="md:grid md:grid-cols-5 flex flex-col gap-2 md:gap-4 items-start md:items-center cursor-pointer p-3 bg-slate-800/50 rounded-lg border border-slate-700 hover:border-slate-600 transition-colors touch-auto">
                          {/* Mobile view - Show labels with data */}
                          <div className="flex justify-between items-center w-full md:hidden">
                            <div className="space-y-2 w-full">
                              <div className="flex justify-between items-center">
                                <span className="text-slate-400 text-sm">
                                  Date:
                                </span>
                                <p className="text-slate-300">
                                  {payment.paymentDate &&
                                  typeof payment.paymentDate.toDate ===
                                    "function"
                                    ? payment.paymentDate
                                        .toDate()
                                        .toLocaleDateString()
                                    : payment.paymentDate instanceof Date
                                    ? payment.paymentDate.toLocaleDateString()
                                    : new Date(
                                        payment.paymentDate
                                      ).toLocaleDateString()}
                                </p>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-slate-400 text-sm">
                                  Type:
                                </span>
                                <p className="text-slate-300">
                                  {payment.paymentType || "Cash"}
                                </p>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-slate-400 text-sm">
                                  Amount:
                                </span>
                                <p className="text-emerald-400 font-medium">
                                  ${(payment.paymentAmount || 0).toFixed(2)}
                                </p>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-slate-400 text-sm">
                                  Balance:
                                </span>
                                <p
                                  className={`font-medium ${
                                    (payment.newBalance || 0) < 0
                                      ? "text-red-400"
                                      : "text-emerald-400"
                                  }`}
                                >
                                  $
                                  {(Number(payment.newBalance) || 0).toFixed(2)}
                                </p>
                              </div>
                            </div>
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                handleDownloadReceipt(payment);
                              }}
                              className="bg-cyan-600/20 hover:bg-cyan-600/30 text-cyan-400 p-1.5 rounded-lg transition-all ml-4"
                              title="Download Receipt"
                            >
                              <Download size={16} />
                            </button>
                          </div>

                          {/* Desktop view - Grid layout */}
                          <p className="text-center text-slate-300 hidden md:block">
                            {payment.paymentDate &&
                            typeof payment.paymentDate.toDate === "function"
                              ? payment.paymentDate
                                  .toDate()
                                  .toLocaleDateString()
                              : payment.paymentDate instanceof Date
                              ? payment.paymentDate.toLocaleDateString()
                              : new Date(
                                  payment.paymentDate
                                ).toLocaleDateString()}
                          </p>
                          <p className="text-center text-slate-300 hidden md:block">
                            {payment.paymentType || "Cash"}
                          </p>
                          <p className="text-center text-emerald-400 font-medium hidden md:block">
                            ${(payment.paymentAmount || 0).toFixed(2)}
                          </p>
                          <p
                            className={`text-center font-medium hidden md:block ${
                              (payment.newBalance || 0) < 0
                                ? "text-red-400"
                                : "text-emerald-400"
                            }`}
                          >
                            ${(Number(payment.newBalance) || 0).toFixed(2)}
                          </p>
                          <div className="flex justify-center hidden md:flex">
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                handleDownloadReceipt(payment);
                              }}
                              className="bg-cyan-600/20 hover:bg-cyan-600/30 text-cyan-400 p-1.5 rounded-lg transition-all"
                              title="Download Receipt"
                            >
                              <Download size={16} />
                            </button>
                          </div>
                        </summary>

                        {/* Expanded details section */}
                        <div className="mt-2 p-4 bg-slate-800/30 rounded-lg border border-slate-700 space-y-2">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Resident Name */}
                            {payment.residentName && (
                              <div className="space-y-1">
                                <p className="text-sm text-slate-400">
                                  Resident
                                </p>
                                <p className="text-slate-300">
                                  {payment.residentName}
                                </p>
                              </div>
                            )}

                            {/* Invoice Number - Always show for invoice payments */}
                            {payment.paymentType === "Invoice" && (
                              <div className="space-y-1">
                                <p className="text-sm text-slate-400">
                                  Invoice Number
                                </p>
                                <p className="text-slate-300">
                                  {payment.invoiceNumber || "N/A"}
                                </p>
                              </div>
                            )}

                            {/* Invoice Week */}
                            {payment.invoiceWeek && (
                              <div className="space-y-1">
                                <p className="text-sm text-slate-400">
                                  Invoice Week
                                </p>
                                <p className="text-slate-300">
                                  {payment.invoiceWeek}
                                </p>
                              </div>
                            )}

                            {/* Funding Source */}
                            {payment.fundingSource && (
                              <div className="space-y-1">
                                <p className="text-sm text-slate-400">
                                  Funding Source
                                </p>
                                <p className="text-slate-300">
                                  {payment.fundingSource}
                                </p>
                              </div>
                            )}

                            {/* Payment Note */}
                            {payment.paymentNote && (
                              <div className="space-y-1">
                                <p className="text-sm text-slate-400">
                                  Payment Note
                                </p>
                                <p className="text-slate-300">
                                  {payment.paymentNote}
                                </p>
                              </div>
                            )}

                            {/* Previous Balance */}
                            {typeof payment.previousBalance !== "undefined" && (
                              <div className="space-y-1">
                                <p className="text-sm text-slate-400">
                                  Previous Balance
                                </p>
                                <p
                                  className={`font-medium ${
                                    payment.previousBalance < 0
                                      ? "text-red-400"
                                      : "text-emerald-400"
                                  }`}
                                >
                                  $
                                  {(
                                    Number(payment.previousBalance) || 0
                                  ).toFixed(2)}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </details>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Modal
        isOpen={isModalOpen}
        onRequestClose={() => {
          setIsModalOpen(false);
          setSelectedFiles([]); // Clear files when closing modal
        }}
        contentLabel="Save Document"
        style={{
          overlay: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
            zIndex: 50,
          },
          content: {
            top: "50%",
            left: "50%",
            right: "auto",
            bottom: "auto",
            marginRight: "-50%",
            transform: "translate(-50%, -50%)",
            background: "rgba(15, 23, 42, 0.9)",
            border: "1px solid rgb(51, 65, 85)",
            borderRadius: "0.75rem",
            width: "90%",
            maxWidth: "600px",
            padding: "0",
            color: "white",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          },
        }}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <Save className="text-blue-400" size={20} />
              </div>
              <h2 className="text-xl font-medium text-white">Save Documents</h2>
            </div>
            <button
              onClick={() => {
                setIsModalOpen(false);
                setSelectedFiles([]);
              }}
              className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
            >
              <X className="text-slate-400 hover:text-slate-300" size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            <div className="relative">
              <input
                type="text"
                id="saveAs"
                value={questionnaire.saveAs}
                onChange={(e) =>
                  setQuestionnaire({ ...questionnaire, saveAs: e.target.value })
                }
                className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors peer"
                placeholder=" "
              />
              <label
                htmlFor="saveAs"
                className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-blue-400 transition-colors"
              >
                Group Name
              </label>
            </div>

            {/* Selected Files List */}
            {selectedFiles.length > 0 && (
              <div className="mt-4 space-y-2 max-h-48 overflow-y-auto">
                {selectedFiles.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between bg-slate-800/50 p-2 rounded-lg"
                  >
                    <span className="text-slate-300 truncate">{file.name}</span>
                    <button
                      onClick={() => removeFile(index)}
                      className="p-1 hover:bg-slate-700 rounded-lg text-red-400 hover:text-red-300"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* File Input Button */}
            <input
              type="file"
              id="fileInput"
              hidden
              multiple
              onChange={handleFileChange}
            />
            <button
              onClick={() => document.getElementById("fileInput").click()}
              className="px-4 py-2 bg-slate-700 text-slate-300 hover:bg-slate-600 hover:text-white rounded-lg flex items-center gap-2"
            >
              <Plus size={20} />
              Add Files
            </button>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-700 flex justify-end gap-3">
            <button
              type="button"
              onClick={() => {
                setIsModalOpen(false);
                setSelectedFiles([]);
              }}
              className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg transition-colors flex items-center gap-2"
            >
              <X size={20} />
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={selectedFiles.length === 0 || !questionnaire.saveAs}
              className={`px-4 py-2 ${
                selectedFiles.length === 0 || !questionnaire.saveAs
                  ? "bg-blue-500/40 cursor-not-allowed"
                  : "bg-blue-500/80 hover:bg-blue-500 cursor-pointer"
              } text-white rounded-lg transition-colors flex items-center gap-2`}
            >
              <Save size={20} />
              Save Files
            </button>
          </div>
        </div>
      </Modal>

      {isDropdownOpen && (
        <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700 space-y-2">
          {dropdownItems.length > 0 ? (
            dropdownItems.map((doc) => (
              <div key={doc.id} className="flex justify-between items-center">
                <p className="text-slate-300">{doc.fileName}</p>
                <a
                  href={doc.fileURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:underline"
                >
                  View
                </a>
              </div>
            ))
          ) : (
            <p className="text-slate-400">No documents uploaded.</p>
          )}
        </div>
      )}

      {showEmploymentModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex justify-center items-center">
          <div className="bg-slate-900/90 border border-slate-700 rounded-xl shadow-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-semibold text-white mb-4">
              Update Employment Status
            </h3>
            <div className="space-y-4">
              <div className="relative">
                <input
                  type="date"
                  value={newEmploymentDate}
                  onChange={(e) => setNewEmploymentDate(e.target.value)}
                  className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-blue-500 transition-colors peer"
                />
                <label className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-blue-400 transition-colors">
                  Date of Status Change
                </label>
              </div>

              {pendingWorkStatusChange !== "Unemployed" && (
                <div className="relative">
                  <input
                    type="text"
                    value={employer}
                    onChange={(e) => setEmployer(e.target.value)}
                    className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-blue-500 transition-colors peer"
                  />
                  <label className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-blue-400 transition-colors">
                    Employer Name (Optional)
                  </label>
                </div>
              )}

              <div className="relative">
                <textarea
                  value={employmentNotes}
                  onChange={(e) => setEmploymentNotes(e.target.value)}
                  className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-blue-500 transition-colors peer min-h-[100px]"
                />
                <label className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-blue-400 transition-colors">
                  Additional Notes (Optional)
                </label>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={cancelWorkStatusChange}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmWorkStatusChange}
                  disabled={!newEmploymentDate}
                  className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Form Completion Modal */}
      {isFormCompletionModalOpen && (
        <FormListModal
          organizationId={organizationId}
          residentId={residentId}
          residentFullName={residentProfile?.fullName || "Unknown"}
          onClose={() => setIsFormCompletionModalOpen(false)}
        />
      )}

      {/* Form Submission View Modal */}
      {viewingFormSubmission && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex justify-center items-center overflow-auto">
          <div className="bg-slate-900/90 border border-slate-700 rounded-xl shadow-lg w-full max-w-2xl">
            <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/10 rounded-lg">
                  <ClipboardList className="text-blue-400" size={20} />
                </div>
                <h3 className="text-xl font-semibold text-white">
                  {viewingFormSubmission.formName}
                </h3>
              </div>
              <button
                onClick={() => setViewingFormSubmission(null)}
                className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
              >
                <X className="text-slate-400 hover:text-slate-300" size={20} />
              </button>
            </div>

            <div className="p-6 max-h-[70vh] overflow-y-auto">
              <div className="space-y-6">
                {/* Metadata */}
                <div className="mb-6 p-4 bg-slate-800/50 rounded-lg">
                  <p className="text-slate-400">
                    Submitted on:{" "}
                    {viewingFormSubmission.dateSubmitted
                      ? new Date(
                          viewingFormSubmission.dateSubmitted.seconds * 1000
                        ).toLocaleString()
                      : viewingFormSubmission.responses?.dateSubmitted
                      ? new Date(
                          viewingFormSubmission.responses.dateSubmitted
                            .seconds * 1000
                        ).toLocaleString()
                      : "Unknown date"}
                  </p>
                  <p className="text-slate-400">
                    Submitted by:{" "}
                    {viewingFormSubmission.submittedBy?.email || "Unknown"}
                  </p>
                </div>

                {/* Form Fields - use metadata and order to drive rendering */}
                {viewingFormSubmission.responses?.elementOrder
                  ? // Use the preserved order if available
                    viewingFormSubmission.responses.elementOrder.map(
                      (uniqueId) => {
                        const metadata =
                          viewingFormSubmission.responses.elementMetadata[
                            uniqueId
                          ];
                        const value =
                          viewingFormSubmission.responses.responses?.[uniqueId];

                        if (!metadata) return null;

                        return (
                          <div
                            key={uniqueId}
                            className="p-4 bg-slate-800/50 rounded-lg mb-4"
                          >
                            <label className="block text-sm font-medium text-slate-400 mb-1">
                              {metadata.label || uniqueId}
                            </label>

                            {/* Render field value based on type */}
                            {metadata.type === "headerImage" ? (
                              <div className="max-w-full mx-auto">
                                {value && value.imageUrl ? (
                                  <img
                                    src={value.imageUrl}
                                    alt="Header Image"
                                    className={`max-w-full ${
                                      value.alignment === "left"
                                        ? "ml-0 mr-auto"
                                        : value.alignment === "right"
                                        ? "mr-0 ml-auto"
                                        : "mx-auto"
                                    } rounded-lg object-contain`}
                                    style={{
                                      maxHeight:
                                        value.size === "small"
                                          ? "8rem"
                                          : value.size === "large"
                                          ? "16rem"
                                          : value.customHeight
                                          ? `${value.customHeight}px`
                                          : "12rem",
                                    }}
                                  />
                                ) : (
                                  <div className="text-white">
                                    Header image not available
                                  </div>
                                )}
                              </div>
                            ) : metadata.type === "textBox" ? (
                              <div className="text-white">
                                {String(value || "")}
                              </div>
                            ) : metadata.type === "checkbox" ||
                              metadata.type === "yesNo" ? (
                              <div className="text-white">
                                {value === true ? "Yes" : "No"}
                              </div>
                            ) : metadata.type === "date" &&
                              value &&
                              typeof value === "object" &&
                              "seconds" in value ? (
                              <div className="text-white">
                                {new Date(
                                  value.seconds * 1000
                                ).toLocaleDateString()}
                              </div>
                            ) : metadata.type === "signature" ? (
                              <div
                                className="rounded-lg overflow-hidden"
                                style={{
                                  backgroundColor: "transparent",
                                  border: "1px solid #374151",
                                }}
                              >
                                <img
                                  src={value}
                                  alt="Signature"
                                  className="max-w-full h-auto"
                                  style={{
                                    mixBlendMode: "darken",
                                    filter: "brightness(0)",
                                  }}
                                />
                              </div>
                            ) : Array.isArray(value) ? (
                              <div className="text-white">
                                {value.map((item, i) => (
                                  <div key={i}>• {item}</div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-white">
                                {String(value || "")}
                              </div>
                            )}
                          </div>
                        );
                      }
                    )
                  : // Fallback to current implementation if order is not available
                    viewingFormSubmission.responses?.elementMetadata &&
                    Object.entries(
                      viewingFormSubmission.responses.elementMetadata
                    ).map(([key, metadata]) => {
                      // Get value from the responses.responses object
                      const value =
                        viewingFormSubmission.responses.responses?.[key];

                      return (
                        <div
                          key={key}
                          className="p-4 bg-slate-800/50 rounded-lg mb-4"
                        >
                          <label className="block text-sm font-medium text-slate-400 mb-1">
                            {metadata.label || key}
                          </label>

                          {/* Render field value based on type */}
                          {metadata.type === "headerImage" ? (
                            <div className="max-w-full mx-auto">
                              {value && value.imageUrl ? (
                                <img
                                  src={value.imageUrl}
                                  alt="Header Image"
                                  className={`max-w-full ${
                                    value.alignment === "left"
                                      ? "ml-0 mr-auto"
                                      : value.alignment === "right"
                                      ? "mr-0 ml-auto"
                                      : "mx-auto"
                                  } rounded-lg object-contain`}
                                  style={{
                                    maxHeight:
                                      value.size === "small"
                                        ? "8rem"
                                        : value.size === "large"
                                        ? "16rem"
                                        : value.customHeight
                                        ? `${value.customHeight}px`
                                        : "12rem",
                                  }}
                                />
                              ) : (
                                <div className="text-white">
                                  Header image not available
                                </div>
                              )}
                            </div>
                          ) : metadata.type === "textBox" ? (
                            <div className="text-white">
                              {String(value || "")}
                            </div>
                          ) : metadata.type === "checkbox" ||
                            metadata.type === "yesNo" ? (
                            <div className="text-white">
                              {value === true ? "Yes" : "No"}
                            </div>
                          ) : metadata.type === "date" &&
                            value &&
                            typeof value === "object" &&
                            "seconds" in value ? (
                            <div className="text-white">
                              {new Date(
                                value.seconds * 1000
                              ).toLocaleDateString()}
                            </div>
                          ) : metadata.type === "signature" ? (
                            <img
                              src={value}
                              alt="Signature"
                              className="max-w-full h-auto border border-slate-700 rounded-lg"
                            />
                          ) : Array.isArray(value) ? (
                            <div className="text-white">
                              {value.map((item, i) => (
                                <div key={i}>• {item}</div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-white">
                              {String(value || "")}
                            </div>
                          )}
                        </div>
                      );
                    })}
              </div>
            </div>

            <div className="px-6 py-4 border-t border-slate-700 flex justify-end gap-3">
              <button
                onClick={() => setViewingFormSubmission(null)}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg transition-colors"
              >
                Close
              </button>
              <button
                onClick={() => {
                  console.log(
                    "organizationData before PDF generation:",
                    organizationData
                  );
                  generateFormPDF(viewingFormSubmission, organizationData);
                }}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                <Download size={18} />
                Download PDF
              </button>
            </div>
          </div>
        </div>
      )}
      {/* MAT Status Change Modal */}
      {showMATStatusModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex justify-center items-center">
          <div className="bg-slate-900/90 border border-slate-700 rounded-xl shadow-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-semibold text-white mb-4">
              Update MAT Status
            </h3>
            <div className="space-y-4">
              <div className="relative">
                <input
                  type="date"
                  value={newMATDate}
                  onChange={(e) => setNewMATDate(e.target.value)}
                  className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-purple-500 transition-colors peer"
                />
                <label className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-purple-400 transition-colors">
                  Date Ended MAT
                </label>
              </div>

              <div className="relative">
                <textarea
                  value={matNotes}
                  onChange={(e) => setMATNotes(e.target.value)}
                  className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-purple-500 transition-colors peer min-h-[100px]"
                />
                <label className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-purple-400 transition-colors">
                  Additional Notes (Optional)
                </label>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={cancelMATStatusChange}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmMATStatusChange}
                  disabled={!newMATDate}
                  className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Probation Status Change Modal */}
      {showProbationStatusModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex justify-center items-center">
          <div className="bg-slate-900/90 border border-slate-700 rounded-xl shadow-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-semibold text-white mb-4">
              Update Probation/Parole Status
            </h3>
            <div className="space-y-4">
              <div className="relative">
                <input
                  type="date"
                  value={newProbationDate}
                  onChange={(e) => setNewProbationDate(e.target.value)}
                  className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-indigo-500 transition-colors peer"
                />
                <label className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-indigo-400 transition-colors">
                  Date Ended Probation/Parole
                </label>
              </div>

              <div className="relative">
                <textarea
                  value={probationNotes}
                  onChange={(e) => setProbationNotes(e.target.value)}
                  className="w-full bg-slate-800/50 rounded-lg px-4 py-2.5 pt-6 text-white border border-slate-600 focus:border-indigo-500 transition-colors peer min-h-[100px]"
                />
                <label className="absolute left-4 top-2 text-xs text-slate-400 peer-focus:text-indigo-400 transition-colors">
                  Additional Notes (Optional)
                </label>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={cancelProbationStatusChange}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmProbationStatusChange}
                  disabled={!newProbationDate}
                  className="px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResidentProfile;
